import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { api } from './api';  // Use centralized API client
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Analytics } from "@vercel/analytics/react";
import { ChevronDownIcon, MagnifyingGlassIcon, StarIcon, ClockIcon, AcademicCapIcon, UserGroupIcon, BookOpenIcon, SparklesIcon, DocumentTextIcon, ArrowUpTrayIcon, CloudArrowUpIcon, MoonIcon, SunIcon, FunnelIcon, BuildingOfficeIcon, AdjustmentsHorizontalIcon, ChevronLeftIcon, ChevronRightIcon, ArrowUpIcon, ArrowDownIcon, Bars3Icon, HomeIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import './App.css';
import './accent-colors.css';
import Clubs from "./Clubs";
import Settings from "./Settings";
import DashboardPage from "./components/Dashboard";
import Landing from "./components/Landing";
import MobileMenu, { MobileMenuItem, MobileMenuSection } from "./components/MobileMenu";
import { CourseCardSkeleton, ClubCardSkeleton, GPATileSkeleton, SkeletonGrid } from "./components/SkeletonLoader";
import { HelpTooltip } from "./components/Tooltip";
import { SuccessCheckmark, Toast, ConfettiAnimation } from "./components/SuccessAnimation";

// Custom hook for hide-on-scroll behavior on mobile
function useHideOnScroll() {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Only apply on mobile (screens smaller than 768px)
      if (window.innerWidth >= 768) {
        setIsVisible(true);
        return;
      }

      // Show header when scrolling up, hide when scrolling down
      if (currentScrollY < lastScrollY || currentScrollY < 50) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return isVisible;
}

// API base URL - now handled by api.js
const API_URL = "https://dvtime-1c6x.onrender.com";

// Auth Context
const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for token in URL (from ClassLink redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    
    if (urlToken) {
      setToken(urlToken);
      localStorage.setItem('token', urlToken);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (token) {
      // Token is automatically added by api.js interceptor
      // Decode token to get user info
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({
          id: payload.user_id,
          email: payload.email,
          student_id: payload.student_id
        });
      } catch (error) {
        console.error('Error decoding token:', error);
        localStorage.removeItem('token');
        setToken(null);
      }
    }
    setLoading(false);
  }, [token]);

  const login = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('token', userToken);
    localStorage.setItem('user', JSON.stringify(userData));
    // Token is automatically added by api.js interceptor
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clear user-specific settings
    localStorage.removeItem('preferredName');
    localStorage.removeItem('pronouns');
    localStorage.removeItem('profileImage');
    // Token removal is handled by api.js interceptor
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return React.useContext(AuthContext);
}

// Theme Context
const ThemeContext = React.createContext();

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        return savedTheme;
      }
      // Check system preference if no saved theme
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
      return 'light';
    }
    return 'light';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {

      if (theme === 'dark') {
        document.documentElement.classList.add('dark');

      } else {
        document.documentElement.classList.remove('dark');

      }
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  // Apply theme immediately on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && theme) {
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }, []);

  return (
    <ThemeContext.Provider value={[theme, setTheme]}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Footer Component
function Footer() {
  const currentYear = new Date().getFullYear();
  const appVersion = "v1.0";
  
  return (
    <footer className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* About Section */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">About DVTime</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              DVTime is a comprehensive academic planning platform designed for Dougherty Valley High School students. 
              Plan your courses, track your GPA, explore clubs, and manage your academic journey all in one place.
            </p>
            <div className="mt-4">
              <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                {appVersion}
              </span>
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <a 
                  href="https://forms.gle/VA6BwBhj3vCMjvAV8" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-[var(--accent-600)] dark:hover:text-[var(--accent-400)] transition-colors"
                >
                  Contact & Support
                </a>
              </li>
              <li>
                <a 
                  href="https://www.dvhigh.net" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-[var(--accent-600)] dark:hover:text-[var(--accent-400)] transition-colors"
                >
                  DVHS Website
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              © {currentYear} DVTime. All rights reserved.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              Made with ❤️ by DVHS students
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Components
function Navbar({ currentPage, onPageChange, onOpenSettings, onOpenMobileMenu }) {
  const { user, logout } = useAuth();
  const [theme, setTheme] = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get preferred name, fallback to first name from full_name, then email username
  let displayName = 'User';
  
  // Check localStorage first for preferred name
  const savedPreferredName = localStorage.getItem('preferredName');
  
  if (savedPreferredName && savedPreferredName.trim()) {
    // Use preferred name from localStorage if set
    displayName = savedPreferredName.trim();
  } else if (user?.preferred_name && user.preferred_name.trim()) {
    // Use preferred name from user object if set
    displayName = user.preferred_name.trim();
  } else if (user?.full_name && user.full_name.trim()) {
    // Extract first name from full name
    displayName = user.full_name.trim().split(' ')[0];
  } else if (user?.email) {
    // Extract username from email (before @)
    displayName = user.email.split('@')[0];
  }

  // Get profile picture
  const profilePicture = localStorage.getItem('profileImage') || user?.profile_picture || null;
  
  // Get initials for fallback
  const getInitials = () => {
    if (savedPreferredName && savedPreferredName.trim()) {
      return savedPreferredName.trim().split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    } else if (user?.full_name) {
      return user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  return (
    <nav className="bg-white/95 backdrop-blur-md dark:bg-gray-900/95 shadow-lg border-b border-gray-200/50 dark:border-gray-800/50 sticky top-0 z-40">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-2">
            {/* Mobile Hamburger Menu */}
            <button 
              onClick={onOpenMobileMenu}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Open menu"
            >
              <Bars3Icon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            </button>
            <AcademicCapIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">DVTime</h1>
          </div>
          
          {user && (
            <>
              {/* Tabs - Hidden on mobile, shown on desktop */}
              <div className="hidden md:flex items-center gap-2 md:gap-3 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => onPageChange('dashboard')}
                  className={`px-3 md:px-4 py-2 rounded-xl text-sm md:text-base font-semibold tracking-tight transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/70 focus-visible:ring-offset-0 motion-safe:hover:scale-[1.02] ${
                    currentPage === 'dashboard' 
                      ? 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-indigo-500/40 shadow-[0_0_0_3px_rgba(99,102,241,0.12)_inset]' 
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800/60'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => onPageChange('courses')}
                  className={`px-3 md:px-4 py-2 rounded-xl text-sm md:text-base font-semibold tracking-tight transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/70 focus-visible:ring-offset-0 motion-safe:hover:scale-[1.02] ${
                    currentPage === 'courses' 
                      ? 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-indigo-500/40 shadow-[0_0_0_3px_rgba(99,102,241,0.12)_inset]' 
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800/60'
                  }`}
                >
                  Course Planner
                </button>
                <button
                  onClick={() => onPageChange('transcript')}
                  className={`px-3 md:px-4 py-2 rounded-xl text-sm md:text-base font-semibold tracking-tight transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/70 focus-visible:ring-offset-0 motion-safe:hover:scale-[1.02] ${
                    currentPage === 'transcript' 
                      ? 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-indigo-500/40 shadow-[0_0_0_3px_rgba(99,102,241,0.12)_inset]' 
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800/60'
                  }`}
                >
                  My Transcript
                </button>
                <button
                  onClick={() => onPageChange('teachers')}
                  className={`px-3 md:px-4 py-2 rounded-xl text-sm md:text-base font-semibold tracking-tight transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/70 focus-visible:ring-offset-0 motion-safe:hover:scale-[1.02] ${
                    currentPage === 'teachers' 
                      ? 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-indigo-500/40 shadow-[0_0_0_3px_rgba(99,102,241,0.12)_inset]' 
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800/60'
                  }`}
                >
                  Teachers
                </button>
                <button
                  onClick={() => onPageChange('clubs')}
                  className={`px-3 md:px-4 py-2 rounded-xl text-sm md:text-base font-semibold tracking-tight transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/70 focus-visible:ring-offset-0 motion-safe:hover:scale-[1.02] ${
                    currentPage === 'clubs' 
                      ? 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-indigo-500/40 shadow-[0_0_0_3px_rgba(99,102,241,0.12)_inset]' 
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800/60'
                  }`}
                >
                  Clubs
                </button>
              </div>
              
              {/* Right-side controls */}
              <div className="flex items-center gap-2 md:gap-3">
                {/* Welcome Badge with Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-100 border border-gray-200 dark:bg-gray-900/60 dark:border-gray-800 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
                  >
                    {/* Profile Picture */}
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                      {profilePicture ? (
                        <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span>{getInitials()}</span>
                      )}
                    </div>
                    <span className="text-sm md:text-base text-gray-600 dark:text-gray-300 hidden sm:inline">
                      Welcome, <span className="text-gray-900 dark:text-gray-100 font-semibold">{displayName}</span>
                    </span>
                  </button>
                  
                  {showDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl z-50">
                      <button
                        onClick={() => {
                          onOpenSettings();
                          setShowDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-t-xl transition-colors"
                      >
                        Profile & Settings
                      </button>
                      <button
                        onClick={() => {
                          logout();
                          setShowDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-b-xl transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Theme Toggle */}
                <button
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/70"
                  aria-label="Toggle dark mode"
                >
                  {theme === 'dark' ? (
                    <SunIcon className="h-5 w-5 text-yellow-400" />
                  ) : (
                    <MoonIcon className="h-5 w-5 text-gray-700" />
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

function TranscriptPreSignupUploader({ onUploaded, onBack }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const progressIntervalRef = useRef(null);
  const [theme, setTheme] = useTheme();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setError('');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a transcript file to upload.');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadStage('Uploading file...');
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Start progress simulation immediately
      progressIntervalRef.current = setInterval(() => {
        setUploadProgress(prev => {
          // Never go above 99% until processing is actually complete
          if (prev >= 99) {
            return 99;
          }
          
          let increment = 1; // Default 1% increment to ensure steady progress
          if (prev < 20) {
            setUploadStage('Uploading file...');
            increment = 1.0; // Slower start
          } else if (prev < 40) {
            setUploadStage('Converting PDF to images...');
            increment = 0.8; // Slower pace
          } else if (prev < 60) {
            setUploadStage('Processing with AI vision...');
            increment = 0.7; // Steady but slower
          } else if (prev < 80) {
            setUploadStage('Extracting course data...');
            increment = 0.6; // Slower but steady
          } else if (prev < 95) {
            setUploadStage('Analyzing transcript structure...');
            increment = 0.4; // Slow down near end
          } else {
            setUploadStage('Finalizing...');
            increment = 0.2; // Crawl to 99%
          }
          
          const newProgress = prev + increment;
          return Math.min(Math.round(newProgress), 99); // Cap at 99%
        });
      }, 1200); // Slower intervals for better UX
      
      // Start the upload (no progress tracking needed since we simulate it)
      const uploadPromise = api.post('/transcript/ocr-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Wait for the actual upload to complete
      const resp = await uploadPromise;
      
      // Clear the simulation and set to complete
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setUploadProgress(100);
      setUploadStage('Complete!');
      
      // Small delay to show completion
      setTimeout(() => {
        onUploaded(resp.data.file_path, resp.data.data);
      }, 500);
      
    } catch (err) {
      // Clear progress simulation on error
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setError(err.response?.data?.detail || 'Failed to upload transcript.');
    } finally {
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        setUploadStage('');
      }, 1000);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 flex items-center justify-center p-4">
      {/* Dark Mode Toggle */}
      <button
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        className="absolute top-4 right-4 p-2 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="Toggle dark mode"
      >
        {theme === 'dark' ? (
          <SunIcon className="h-5 w-5 text-yellow-400" />
        ) : (
          <MoonIcon className="h-5 w-5 text-gray-700" />
        )}
      </button>
      
      <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 relative">
        {/* Back to Login Button */}
        {onBack && (
          <button
            onClick={onBack}
            className="absolute top-4 left-4 flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            aria-label="Back to login"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back to Login</span>
          </button>
        )}
        
        <div className="text-center mb-8 mt-8">
          <DocumentTextIcon className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Upload Your Transcript</h2>
          <p className="text-gray-600 dark:text-gray-300">To sign up, please upload your transcript file first.</p>
        </div>
        {error && (
          <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-600 dark:text-red-200 px-4 py-3 rounded-lg mb-4">{error}</div>
        )}
        <form onSubmit={handleUpload} className="space-y-6">
          {/* Modern Drag & Drop Upload Area */}
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
              dragActive 
                ? 'border-indigo-400 bg-indigo-50 dark:border-indigo-500 dark:bg-indigo-900/20' 
                : file 
                  ? 'border-green-400 bg-green-50 dark:border-green-500 dark:bg-green-900/20' 
                  : 'border-gray-300 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-500 hover:bg-gray-50 dark:hover:bg-gray-800/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept=".pdf,.txt,.doc,.docx"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              id="file-upload"
            />
            
            <div className="space-y-4">
              {file ? (
                <>
                  <div className="flex items-center justify-center">
                    <DocumentTextIcon className="h-12 w-12 text-green-600" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-green-800 dark:text-green-200">{file.name}</p>
                    <p className="text-sm text-green-600 dark:text-green-300">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
                  >
                    Remove file
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-center">
                    <CloudArrowUpIcon className="h-12 w-12 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">
                      Drop your transcript here
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      or click to browse files
                    </p>
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    Supports PDF, TXT, DOC, DOCX (max 10MB)
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          {uploading && (
            <div className="space-y-3">
              <div className="flex justify-between text-sm text-gray-700 dark:text-gray-200">
                <span className="font-medium">{uploadStage}</span>
                <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full transition-all duration-500 ease-out relative"
                  style={{ width: `${uploadProgress}%` }}
                >
                  <div className="absolute inset-0 bg-white opacity-30 animate-pulse"></div>
                </div>
              </div>
              <div className="flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                <span>Please wait while we process your transcript...</span>
              </div>
            </div>
          )}

          <button 
            type="submit" 
            disabled={uploading || !file} 
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors"
          >
            {uploading ? (
              <span>Processing...</span>
            ) : (
              'Upload Transcript'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function LoginForm({ initialTranscriptData = null, isLogin: initialIsLogin = true }) {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(initialIsLogin);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    grade: 9,
    transcript_file_path: initialTranscriptData ? 'pre-uploaded' : '',
    transcript_data: initialTranscriptData || null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transcriptUploaded, setTranscriptUploaded] = useState(!!initialTranscriptData);
  const { login } = useAuth();
  const [theme, setTheme] = useTheme();

  // Update isLogin state when route changes
  useEffect(() => {
    setIsLogin(initialIsLogin);
  }, [initialIsLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : {
            email: formData.email,
            password: formData.password,
            full_name: formData.full_name,
            grade: formData.grade,
            transcript_file_path: formData.transcript_file_path,
            transcript_data: formData.transcript_data
          };
      const response = await api.post(endpoint, payload);
      login(response.data.user, response.data.token);
      // Navigate to dashboard after successful login/register
      navigate('/dashboard');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'An error occurred';
      setError(errorMsg.endsWith('.') ? errorMsg : `${errorMsg}.`);
    } finally {
      setLoading(false);
    }
  };

  // Transcript upload is now optional - removed mandatory upload step

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 flex items-center justify-center p-4 dark:text-gray-100">
      {/* Dark Mode Toggle */}
      <button
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        className="absolute top-4 right-4 p-2 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="Toggle dark mode"
      >
        {theme === 'dark' ? (
          <SunIcon className="h-5 w-5 text-yellow-400" />
        ) : (
          <MoonIcon className="h-5 w-5 text-gray-700" />
        )}
      </button>
      
      <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <AcademicCapIcon className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">DVTime</h2>
          <p className="text-gray-600 dark:text-gray-300 mt-2">Smarter Decisions. Stronger Schedules.</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">San Ramon Valley USD</p>
        </div>
        {error && (
          <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-600 dark:text-red-200 px-4 py-3 rounded-lg mb-4">{error}</div>
        )}
        {/* Manual Login/Register */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {!isLogin && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Full Name</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Grade</label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  value={formData.grade}
                  onChange={(e) => setFormData({...formData, grade: parseInt(e.target.value)})}
                >
                  <option value={9}>9th Grade (Freshman)</option>
                  <option value={10}>10th Grade (Sophomore)</option>
                  <option value={11}>11th Grade (Junior)</option>
                  <option value={12}>12th Grade (Senior)</option>
                </select>
              </div>
            </>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Email</label>
            <input
              type="email"
              required
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Password</label>
            <input
              type="password"
              required
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
        </form>
        <div className="mt-4 text-center">
          <span className="text-gray-600 dark:text-gray-400 text-sm">
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              className="text-[var(--accent-600)] dark:text-[var(--accent-400)] hover:underline font-medium"
              onClick={() => navigate(isLogin ? '/register' : '/login')}
            >
              {isLogin ? 'Register' : 'Sign In'}
            </button>
          </span>
        </div>
        <div className="mt-3 text-center">
          <button
            type="button"
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm hover:underline"
            onClick={() => navigate('/landing')}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

function TranscriptUploader() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setError('Please select a transcript file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      // Use the OCR upload endpoint for file processing
      const response = await api.post('/transcript/ocr-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data && response.data.data) {
        setSuccess(true);
        setSelectedFile(null);
        setFileName('');
        
        // Navigate to the signup page with the parsed transcript data
        window.location.href = `/?signup=true&transcript_data=${encodeURIComponent(JSON.stringify(response.data.data))}`;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 
              err.response?.data?.message || 
              'Failed to process transcript. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
        <CloudArrowUpIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-green-900 mb-2">Transcript Uploaded Successfully!</h3>
        <p className="text-green-700 mb-4">Your transcript has been analyzed and saved to your profile.</p>
        <button
          onClick={() => setSuccess(false)}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors"
        >
          Upload Another
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-8">
      <div className="text-center mb-8">
        <DocumentTextIcon className="h-16 w-16 text-indigo-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Upload Your Transcript</h2>
        <p className="text-gray-600 dark:text-gray-300">
          Upload your Infinite Campus transcript to get personalized course recommendations
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload Transcript File
          </label>
          <div className="flex items-center justify-center w-full">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <ArrowUpTrayIcon className="h-8 w-8 text-gray-400 mb-2" />
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">PDF, images, or text files</p>
              </div>
              <input 
                type="file" 
                className="hidden" 
                accept=".pdf,.png,.jpg,.jpeg,.txt,.doc,.docx"
                onChange={handleFileUpload}
              />
            </label>
          </div>
          {fileName && (
            <p className="text-sm text-gray-600 mt-2">Selected: {fileName}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={uploading}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
        >
          {uploading ? 'Analyzing Transcript...' : 'Upload & Analyze Transcript'}
        </button>
      </form>

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">How to get your transcript:</h3>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li>Login to Infinite Campus</li>
          <li>Go to "Student Information" → "Documents"</li>
          <li>Find your unofficial transcript</li>
          <li>Download as PDF or take a screenshot, or copy the text</li>
        </ol>
        <p className="text-xs text-blue-700 mt-2">
          <strong>Tip:</strong> We now support PDF files and images using AI vision technology for better accuracy!
        </p>
      </div>
    </div>
  );
}

// Future Term Card Component
function FutureTermCard({ term, onAddCourse, onRemoveCourse, allCourses }) {
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [courseForm, setCourseForm] = useState({
    courseId: '',
    expectedGrade: 'A'
  });

  const departments = [
    'English', 'Mathematics', 'Science', 'Social Science', 
    'World Language', 'Fine and Applied Arts (VAPA)', 'Physical Education'
  ];

  const grades = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'F'];

  // Filter courses based on search and department
  const filteredCourses = allCourses.filter(course => {
    const matchesSearch = !courseSearchTerm || 
      course.name.toLowerCase().includes(courseSearchTerm.toLowerCase()) ||
      course.code.toLowerCase().includes(courseSearchTerm.toLowerCase());
    const matchesDepartment = !selectedDepartment || course.department === selectedDepartment;
    return matchesSearch && matchesDepartment;
  });

  const handleAddCourse = () => {
    const selectedCourse = allCourses.find(c => c.id === courseForm.courseId);
    if (selectedCourse) {
      onAddCourse(term.id, {
        name: selectedCourse.name,
        code: selectedCourse.code,
        department: selectedCourse.department,
        credits: selectedCourse.credits || 5,
        expectedGrade: courseForm.expectedGrade,
        type: selectedCourse.type || 'Regular'
      });
      setCourseForm({ courseId: '', expectedGrade: 'A' });
      setCourseSearchTerm('');
      setSelectedDepartment('');
      setShowAddCourse(false);
      setCourseDropdownOpen(false);
    }
  };

  return (
    <div className="border border-green-200 dark:border-green-700 rounded-xl p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <h5 className="font-semibold text-gray-900 dark:text-white">
            Grade {term.grade} - Term {term.term}
          </h5>
          <span className="px-2 py-1 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200 text-xs rounded-full font-medium">
            What-If
          </span>
        </div>
        <button
          onClick={() => setShowAddCourse(!showAddCourse)}
          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors flex items-center space-x-1"
        >
          <span>+</span>
          <span>Add Course</span>
        </button>
      </div>

      {/* Existing courses in this term */}
      {term.courses.length > 0 && (
        <div className="space-y-2 mb-3">
          {term.courses.map(course => (
            <div key={course.id} className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg border border-green-100 dark:border-green-800">
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">{course.Course}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{course.code} • {course.Department}</div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                  course.Mark === 'A' || course.Mark === 'A+' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200' :
                  course.Mark === 'A-' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200' :
                  course.Mark.startsWith('B') ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200' :
                  course.Mark.startsWith('C') ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' :
                  'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200'
                }`}>
                  {course.Mark}
                </span>
                <button
                  onClick={() => onRemoveCourse(course.id)}
                  className="text-red-500 hover:text-red-700 p-1 rounded transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add course form */}
      {showAddCourse && (
        <div className="space-y-3 border-t border-green-200 dark:border-green-700 pt-3">
          {/* Course Selection Dropdown */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Select Course
            </label>
            <button
              type="button"
              onClick={() => setCourseDropdownOpen(!courseDropdownOpen)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-800 dark:text-white text-left flex items-center justify-between"
            >
              <span className={courseForm.courseId ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                {courseForm.courseId ? allCourses.find(c => c.id === courseForm.courseId)?.name || 'Select a course...' : 'Select a course...'}
              </span>
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {courseDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-64 overflow-hidden">
                {/* Search and Filter Header */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
                  <input
                    type="text"
                    placeholder="Search courses..."
                    value={courseSearchTerm}
                    onChange={(e) => setCourseSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                  />
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Departments</option>
                    {departments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
                
                {/* Course List */}
                <div className="max-h-48 overflow-y-auto">
                  {filteredCourses.length > 0 ? (
                    filteredCourses.map(course => (
                      <button
                        key={course.id}
                        onClick={() => {
                          setCourseForm({...courseForm, courseId: course.id});
                          setCourseDropdownOpen(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">{course.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {course.code} • {course.department}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                      No courses found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Expected Grade */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Expected Grade
            </label>
            <select
              value={courseForm.expectedGrade}
              onChange={(e) => setCourseForm({...courseForm, expectedGrade: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-800 dark:text-white"
            >
              {grades.map(grade => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => {
                setShowAddCourse(false);
                setCourseDropdownOpen(false);
                setCourseForm({ courseId: '', expectedGrade: 'A' });
                setCourseSearchTerm('');
                setSelectedDepartment('');
              }}
              className="px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddCourse}
              disabled={!courseForm.courseId}
              className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
            >
              Add Course
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// GPA Info Tooltip Component - Using HelpTooltip
function GPAInfoTooltip({ type }) {
  const gpaInfo = {
    weighted: "Includes all courses from grades 9-12 with a +1.0 bonus for AP, Honors, and College courses (C or better). Formula: Σ(grade points + bonus) × credits / Σ credits",
    unweighted: "Includes all courses from grades 9-12 with no bonus points. All letter grades use base points (A=4.0, B=3.0, C=2.0, D=1.0, F=0.0). Formula: Σ(grade points × credits) / Σ credits",
    weighted_10_12: "UC/CSU calculation: Only A-G courses from grades 10-12 with +1.0 bonus for AP, Honors, and College courses (C or better). Excludes PE, electives, and non-academic courses.",
    cal_grant: "California Cal Grant calculation: Academic courses only from grades 10-12, unweighted. Used for Cal Grant eligibility. Excludes PE, non-academic electives, and support courses."
  };
  
  return (
    <HelpTooltip 
      content={gpaInfo[type]}
      position="left"
    />
  );
}

// Future Term Selector Component
function FutureTermSelector({ onAddTerm, existingTerms }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState('12');
  const [selectedTerm, setSelectedTerm] = useState('1');
  const dropdownRef = useRef(null);

  const grades = ['9', '10', '11', '12'];
  const terms = ['1', '2'];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && 
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleAddTerm = () => {
    const termKey = `${selectedGrade}-${selectedTerm}`;
    if (!existingTerms.has(termKey)) {
      onAddTerm(parseInt(selectedGrade), parseInt(selectedTerm));
      setShowDropdown(false);
    }
  };

  const isTermExists = (grade, term) => {
    return existingTerms.has(`${grade}-${term}`);
  };

  return (
    <div className="relative z-30">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-sm font-semibold rounded-lg transition-all shadow-md hover:shadow-lg hover:scale-105 flex items-center space-x-2"
      >
        <span className="text-base">+</span>
        <span>Add What If? Term</span>
        <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${showDropdown ? 'rotate-180' : ''}`} />
      </button>

      {showDropdown && (
        <div 
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 border-2 border-green-300 dark:border-green-600 rounded-xl shadow-2xl p-5 z-30"
        >
          <div className="space-y-4">
            <div className="text-base font-bold text-gray-900 dark:text-white flex items-center space-x-2">
              <span className="text-green-600 dark:text-green-400">📅</span>
              <span>Add What If? Term</span>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Grade</label>
                <select
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(e.target.value)}
                  className="w-full px-3 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white font-medium transition-all"
                >
                  {grades.map(grade => (
                    <option key={grade} value={grade}>Grade {grade}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Term</label>
                <select
                  value={selectedTerm}
                  onChange={(e) => setSelectedTerm(e.target.value)}
                  className="w-full px-3 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white font-medium transition-all"
                >
                  {terms.map(term => (
                    <option key={term} value={term}>Term {term}</option>
                  ))}
                </select>
              </div>
            </div>

            {isTermExists(selectedGrade, selectedTerm) && (
              <div className="text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 p-3 rounded-lg border border-amber-300 dark:border-amber-600">
                ⚠️ This term already exists in your transcript or what-if terms.
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-2">
              <button
                onClick={() => setShowDropdown(false)}
                className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTerm}
                disabled={isTermExists(selectedGrade, selectedTerm)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-bold rounded-lg transition-all shadow-md hover:shadow-lg disabled:shadow-none"
              >
                ✓ Add Term
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Future Term Add Course Component (for inline adding in transcript table)
function FutureTermAddCourse({ termId, onAddCourse, allCourses }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [showCustomCourseModal, setShowCustomCourseModal] = useState(false);
  const [customCourse, setCustomCourse] = useState({ 
    name: '', 
    credits: '', 
    courseType: 'online-hs', // 'online-hs' or 'community-college'
    level: 'regular' // 'regular', 'honors', or 'ap' (only for online-hs)
  });
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && 
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Filter courses based on search (same as teacher review)
  const getFilteredCourses = () => {
    let filtered = allCourses || [];

    if (courseSearchTerm) {
      filtered = filtered.filter(course =>
        course && course.name && course.code &&
        (course.name.toLowerCase().includes(courseSearchTerm.toLowerCase()) ||
        course.code.toLowerCase().includes(courseSearchTerm.toLowerCase()))
      );
    }

    return filtered; // Show all matching courses
  };

  const handleCourseSelect = (course) => {
    onAddCourse(termId, {
      name: course.name,
      code: course.code,
      department: course.department,
      credits: 5.0,  // All high school courses are 5 credits
      expectedGrade: 'A',
      type: course.type || 'Regular',
      isCustom: false
    });
    setShowDropdown(false);
    setCourseSearchTerm('');
  };

  const handleCustomCourseClick = () => {
    setShowDropdown(false);
    setShowCustomCourseModal(true);
    setCustomCourse({ 
      name: '', 
      credits: '', 
      courseType: 'online-hs', 
      level: 'regular' 
    });
  };

  const handleCustomCourseSubmit = () => {
    if (!customCourse.name.trim() || !customCourse.credits || parseFloat(customCourse.credits) <= 0) {
      alert('Please enter a valid course name and credits (must be greater than 0)');
      return;
    }

    // Determine the Type field based on course type and level
    let courseType = 'Custom';
    if (customCourse.courseType === 'community-college') {
      courseType = 'College';
    } else if (customCourse.courseType === 'online-hs') {
      if (customCourse.level === 'ap') {
        courseType = 'AP';
      } else if (customCourse.level === 'honors') {
        courseType = 'Honors';
      } else {
        courseType = 'Regular';
      }
    }

    onAddCourse(termId, {
      name: customCourse.name.trim(),
      code: 'CUSTOM',
      department: customCourse.courseType === 'community-college' ? 'Community College' : 'Online High School',
      credits: parseFloat(customCourse.credits),
      expectedGrade: 'A',
      type: courseType,
      isCustom: true
    });
    setShowCustomCourseModal(false);
    setCustomCourse({ 
      name: '', 
      credits: '', 
      courseType: 'online-hs', 
      level: 'regular' 
    });
  };

  const buttonRef = useRef(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });

  // Update button position when dropdown opens
  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 384; // w-96 = 384px
      
      // Calculate position relative to document (not viewport) for scrolling
      let left = rect.right + window.scrollX - dropdownWidth;
      
      // If dropdown would go off left edge, position it from the left of button
      if (left < window.scrollX + 10) {
        left = rect.left + window.scrollX;
      }
      
      // If dropdown would go off right edge, align to right edge
      if (left + dropdownWidth > window.scrollX + window.innerWidth - 10) {
        left = window.scrollX + window.innerWidth - dropdownWidth - 10;
      }
      
      setButtonPosition({
        top: rect.bottom + window.scrollY + 8,
        left: left
      });
    }
  }, [showDropdown]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setShowDropdown(!showDropdown)}
        className="px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-sm font-semibold rounded-lg transition-all shadow-md hover:shadow-lg hover:scale-105 flex items-center space-x-1"
      >
        <span className="text-base">+</span>
        <span>Add Course</span>
      </button>

      {showDropdown && buttonPosition.top > 0 && ReactDOM.createPortal(
        <div 
          ref={dropdownRef}
          className="absolute w-96 bg-white dark:bg-gray-800 border-2 border-green-300 dark:border-green-600 rounded-xl shadow-2xl overflow-hidden"
          style={{ 
            zIndex: 10001,
            top: `${buttonPosition.top}px`,
            left: `${buttonPosition.left}px`
          }}
        >
          {/* Search Header */}
          <div className="p-4 border-b-2 border-gray-200 dark:border-gray-700">
            <div className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
              <span className="text-green-600 dark:text-green-400">📚</span>
              <span>Add Course to What If? Term</span>
            </div>
            <input
              type="text"
              placeholder="Search by course name or code..."
              value={courseSearchTerm}
              onChange={(e) => setCourseSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white font-medium transition-all"
            />
          </div>

          {/* Custom Course Button */}
          <div className="p-3 border-b-2 border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
            <button
              type="button"
              onClick={handleCustomCourseClick}
              className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg hover:scale-105 flex items-center justify-center space-x-2"
            >
              <span className="text-lg">✏️</span>
              <span>Create Custom Course</span>
            </button>
          </div>

          {/* Course List */}
          <div className="max-h-72 overflow-y-auto">
            {getFilteredCourses().length > 0 ? (
              getFilteredCourses().map(course => (
                <button
                  key={course.id}
                  type="button"
                  onClick={() => handleCourseSelect(course)}
                  className="w-full px-4 py-3 text-left hover:bg-green-50 dark:hover:bg-green-900/20 border-b border-gray-100 dark:border-gray-700 last:border-b-0 focus:outline-none focus:bg-green-50 dark:focus:bg-green-900/20 transition-colors"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {course.name}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {course.code} • {course.department}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
                <div className="text-2xl mb-2">🔍</div>
                <div className="font-medium">No courses found</div>
                <div className="text-xs mt-1">Try adjusting your search</div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Custom Course Modal */}
      {showCustomCourseModal && ReactDOM.createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000]"
            onClick={() => setShowCustomCourseModal(false)}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-[10001] p-4">
            <div 
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-2 border-purple-300 dark:border-purple-600 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b-2 border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-t-2xl">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">✏️</span>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">Create Custom Course</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Enter course details below</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-5">
                {/* Course Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Course Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Independent Study, Online Course"
                    value={customCourse.name}
                    onChange={(e) => setCustomCourse({ ...customCourse, name: e.target.value })}
                    className="w-full px-4 py-3 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white font-medium transition-all"
                    autoFocus
                  />
                </div>

                {/* Course Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Course Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={customCourse.courseType}
                    onChange={(e) => setCustomCourse({ ...customCourse, courseType: e.target.value, level: e.target.value === 'community-college' ? 'college' : 'regular' })}
                    className="w-full px-4 py-3 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white font-medium transition-all"
                  >
                    <option value="online-hs">Online High School</option>
                    <option value="community-college">Community College</option>
                  </select>
                </div>

                {/* Level (only for Online High School) */}
                {customCourse.courseType === 'online-hs' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Course Level <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={customCourse.level}
                      onChange={(e) => setCustomCourse({ ...customCourse, level: e.target.value })}
                      className="w-full px-4 py-3 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white font-medium transition-all"
                    >
                      <option value="regular">Regular</option>
                      <option value="honors">Honors</option>
                      <option value="ap">AP</option>
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      💡 Honors and AP courses receive +1.0 GPA weighting bonus
                    </p>
                  </div>
                )}

                {/* Credits */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Credits <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    placeholder={customCourse.courseType === 'community-college' ? '10.0' : '5.0'}
                    min="0.1"
                    step="0.1"
                    value={customCourse.credits}
                    onChange={(e) => setCustomCourse({ ...customCourse, credits: e.target.value })}
                    className="w-full px-4 py-3 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white font-medium transition-all"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    💡 Standard high school courses: 5.0 credits • College courses: 10.0 credits
                  </p>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <span className="text-blue-600 dark:text-blue-400 text-sm">ℹ️</span>
                    <p className="text-xs text-blue-800 dark:text-blue-300">
                      After adding the course, you can select the expected grade from the dropdown. The GPA will be calculated based on the credits you specify.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-2xl flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCustomCourseModal(false)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-semibold rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCustomCourseSubmit}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg"
                >
                  Add Course
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

function AGProgressModal({ transcriptData, onClose }) {

  // Handle escape key
  useEffect(() => {

    const handleEscape = (e) => {
      if (e.key === 'Escape') {

        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {

      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  if (!transcriptData || !transcriptData.transcript) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={onClose}>
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
          <p className="text-center text-gray-500 dark:text-gray-400">No transcript data available</p>
        </div>
      </div>,
      document.body
    );
  }

  const courses = transcriptData.transcript.courses || [];
  const agRequirements = {
    'A': { name: 'History/Social Science', required: 2, completed: 0, courses: [] },
    'B': { name: 'English', required: 4, completed: 0, courses: [] },
    'C': { name: 'Mathematics', required: 3, completed: 0, courses: [] },
    'D': { name: 'Laboratory Science', required: 2, completed: 0, courses: [] },
    'E': { name: 'Language Other Than English', required: 2, completed: 0, courses: [] },
    'F': { name: 'Visual & Performing Arts', required: 1, completed: 0, courses: [] },
    'G': { name: 'College-Prep Elective', required: 1, completed: 0, courses: [] }
  };

  courses.forEach(course => {
    const courseName = course.Course || course.course_name;
    const grade = course.Mark || course.grade;
    const credits = course.Credits || course.credits || 5;
    const agTag = course['A-G'] || course.a_g_requirement || course.a_g_tag;
    
    if (agTag && agTag !== 'None' && agTag !== '' && agRequirements[agTag]) {
      const passingGrades = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-'];
      if (passingGrades.includes(grade)) {
        const years = credits / 10;
        agRequirements[agTag].completed += years;
        agRequirements[agTag].courses.push({ name: courseName, grade, credits });
      }
    }
  });

  const totalRequired = Object.values(agRequirements).reduce((sum, req) => sum + req.required, 0);
  const totalCompleted = Object.values(agRequirements).reduce((sum, req) => sum + Math.min(req.completed, req.required), 0);
  const overallProgress = Math.round((totalCompleted / totalRequired) * 100);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">A-G Progress Details</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="flex flex-col items-center mb-8">
            <div className="relative w-32 h-32">
              <svg className="transform -rotate-90 w-32 h-32">
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="none" className="text-gray-200 dark:text-gray-700" />
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="none" strokeDasharray={`${2 * Math.PI * 56}`} strokeDashoffset={`${2 * Math.PI * 56 * (1 - overallProgress / 100)}`} className="text-purple-600 dark:text-purple-400 transition-all duration-1000" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">{overallProgress}%</span>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Overall A-G Progress</p>
            <p className="text-xs text-gray-500 dark:text-gray-500">{totalCompleted.toFixed(1)} of {totalRequired} years completed</p>
          </div>

          <div className="space-y-4">
            {Object.entries(agRequirements).map(([key, req]) => {
              const progress = Math.min((req.completed / req.required) * 100, 100);
              return (
                <div key={key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">{key} - {req.name}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{req.completed.toFixed(1)} / {req.required} years</p>
                    </div>
                    <span className="text-sm font-bold text-purple-600 dark:text-purple-400">{Math.round(progress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                    <div className="bg-purple-600 dark:bg-purple-400 h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                  </div>
                  {req.courses.length > 0 && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      <p className="font-medium mb-1">Completed courses:</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        {req.courses.map((course, idx) => (
                          <li key={idx}>{course.name} ({course.grade})</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-sm text-yellow-800 dark:text-yellow-200 text-center">
              <strong>Note:</strong> These requirements may not exactly match DVHS requirements. Please verify with your counselor.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function TranscriptViewer({ onPageChange }) {
  const [transcriptData, setTranscriptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadingInfiniteCampus, setLoadingInfiniteCampus] = useState(false);
  const [showTranscriptImages, setShowTranscriptImages] = useState(false);
  const [transcriptImages, setTranscriptImages] = useState([]);
  const [showAGModal, setShowAGModal] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [showAddSchoolModal, setShowAddSchoolModal] = useState(false);
  const [newSchool, setNewSchool] = useState({ name: '', type: 'online-hs' });
  
  // What If? mode state
  const [whatIfMode, setWhatIfMode] = useState(false);
  const [modifiedCourses, setModifiedCourses] = useState([]);
  const [whatIfResults, setWhatIfResults] = useState(null);
  const [calculatingGPA, setCalculatingGPA] = useState(false);
  
  // Future courses state
  const [futureCourses, setFutureCourses] = useState([]);
  const [showAddFutureCourse, setShowAddFutureCourse] = useState(false);
  const [futureTerms, setFutureTerms] = useState([]);
  
  // Drag and drop state for all courses (future and original)
  const [draggedCourse, setDraggedCourse] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverTerm, setDragOverTerm] = useState(null);
  const [dragPreviewPosition, setDragPreviewPosition] = useState(null);
  
  // GPA tooltip state
  const [openTooltip, setOpenTooltip] = useState(null);
  
  // Course data for dropdown
  const [allCourses, setAllCourses] = useState([]);
  
  // Collapsible terms state
  const [collapsedTerms, setCollapsedTerms] = useState(new Set());
  
  // GPA Visibility settings from localStorage
  const [gpaVisibility, setGpaVisibility] = useState(() => {
    const saved = localStorage.getItem('gpaVisibility');
    return saved ? JSON.parse(saved) : {
      weighted: true,
      unweighted: true,
      tenTwelve: true,
      calGrant: true
    };
  });

  useEffect(() => {
    checkExistingTranscript();
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await api.get('/courses');
      setAllCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      // Fallback to mock data if fetch fails
      setAllCourses([
        { id: 1, name: 'AP Calculus BC', code: 'MATH401', department: 'Mathematics', credit: 5 },
        { id: 2, name: 'AP Physics C', code: 'PHYS301', department: 'Science', credit: 5 },
        { id: 3, name: 'AP English Literature', code: 'ENG401', department: 'English', credit: 5 },
        { id: 4, name: 'AP Chemistry', code: 'CHEM301', department: 'Science', credit: 5 },
        { id: 5, name: 'AP US History', code: 'HIST301', department: 'Social Studies', credit: 5 },
        { id: 6, name: 'Algebra 2', code: 'MATH201', department: 'Mathematics', credit: 5 },
        { id: 7, name: 'Biology', code: 'BIO101', department: 'Science', credit: 5 },
        { id: 8, name: 'Spanish 3', code: 'SPAN301', department: 'World Language', credit: 5 },
        { id: 9, name: 'Art History', code: 'ART201', department: 'Fine and Applied Arts (VAPA)', credit: 5 },
        { id: 10, name: 'Physical Education', code: 'PE101', department: 'Physical Education', credit: 2.5 }
      ]);
    }
  };

  const checkExistingTranscript = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get the auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      const response = await api.get('/transcript/data');

      if (response.data && (response.data.transcript_data || response.data.student_info)) {
        const transcript = response.data.transcript_data || { courses: [] };
        const studentInfo = response.data.student_info || {};
        
        // Debug: Log the transcript structure


        // Process courses - they should already be normalized by the backend
        let flattenedCourses = [];
        if (transcript.courses && Array.isArray(transcript.courses)) {
          // Check if courses are already flattened (new format) or nested (old format)
          const firstCourse = transcript.courses[0];




          if (firstCourse && firstCourse.Course) {
            // Already flattened and normalized
            flattenedCourses = transcript.courses;

          } else {
            // Legacy nested format - flatten it
            transcript.courses.forEach(term => {
              if (term.courses && Array.isArray(term.courses)) {
                term.courses.forEach(course => {
                  flattenedCourses.push({
                    ...course,
                    Course: course.name,
                    Grade: term.grade,
                    Term: term.term,
                    Year: term.year,
                    Mark: course.mark,
                    Credits: course.credit,
                    Weight: course.weight
                  });
                });
              }
            });
          }
        }
        
        // Update transcript with flattened courses
        const updatedTranscript = {
          ...transcript,
          courses: flattenedCourses,
          // Explicitly preserve gpa_summary
          gpa_summary: transcript.gpa_summary || {}
        };
        
        // Debug: Log unique grades found
        const uniqueGrades = [...new Set(flattenedCourses.map(c => c.Grade))];


        setTranscriptData({
          transcript: updatedTranscript,
          student_info: studentInfo,
          // Include any additional data from the response
          ...response.data
        });
        
        // Collapse all terms by default
        const groupedCourses = {};
        flattenedCourses.forEach(course => {
          const grade = course.Grade || course.grade || 'Unknown';
          const term = course.Term || course.term || 'Unknown';
          const institution = course.Institution || course.institution || 'Unknown';
          const key = `${grade}|${term}|${institution}`;
          groupedCourses[key] = true;
        });
        setCollapsedTerms(new Set(Object.keys(groupedCourses)));
        
        // If we have transcript data but no courses, show a warning
        if (transcript && (!transcript.courses || transcript.courses.length === 0)) {
          console.warn('Transcript data exists but no courses found');
          setError('Transcript found but no course data could be extracted. Please try uploading your transcript again.');
        }
      } else {

        setError('No transcript data found. Please upload your transcript.');
      }
    } catch (err) {
      console.error('Error fetching transcript:', {
        error: err,
        response: err.response?.data,
        status: err.response?.status
      });
      
      if (err.response) {
        // Server responded with an error status code
        if (err.response.status === 401) {
          setError('Session expired. Please log in again.');
        } else if (err.response.status === 404) {
          setError('No transcript data found. Please upload your transcript.');
        } else if (err.response.status === 400 && err.response.data?.detail?.includes('not fully parsed')) {
          setError('Your transcript is still being processed. Please wait a moment and refresh the page.');
        } else if (err.response.data?.detail) {
          setError(`Error: ${err.response.data.detail}`);
        } else {
          setError('Failed to load transcript data. Please try again later.');
        }
      } else if (err.request) {
        // Request was made but no response received
        setError('Unable to connect to the server. Please check your internet connection.');
      } else {
        // Something else happened
        setError(`Error: ${err.message || 'An unknown error occurred'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Initialize modified courses when transcript data loads
  useEffect(() => {
    if (transcriptData?.transcript?.courses) {
      setModifiedCourses([...transcriptData.transcript.courses]);
    }
  }, [transcriptData]);

  // Calculate GPA for current courses
  const calculateCurrentGPA = async (courses) => {
    try {
      setCalculatingGPA(true);
      const response = await api.post('/gpa/calculate', {
        courses: courses
      });
      return response.data.gpa_summary;
    } catch (error) {
      console.error('Error calculating GPA:', error);
      alert('Failed to calculate GPA. Please check your connection and try again.');
      return null;
    } finally {
      setCalculatingGPA(false);
    }
  };

  // Handle adding a new school
  const handleAddSchool = async () => {
    if (!newSchool.name.trim()) {
      alert('Please enter a school name');
      return;
    }

    try {
      await api.post('/user/schools', {
        school_name: newSchool.name,
        school_type: newSchool.type
      });

      // Refresh transcript data to get updated schools list
      await checkExistingTranscript();
      
      setShowAddSchoolModal(false);
      setNewSchool({ name: '', type: 'online-hs' });
      alert('School added successfully!');
    } catch (error) {
      console.error('Error adding school:', error);
      alert('Failed to add school. Please try again.');
    }
  };

  // Handle What If? mode toggle
  const toggleWhatIfMode = async () => {
    if (whatIfMode) {
      // Exit What If? mode - reset to original data
      setWhatIfMode(false);
      setModifiedCourses([...transcriptData.transcript.courses]);
      setWhatIfResults(null);
      setFutureCourses([]);
      setFutureTerms([]);
    } else {
      // Enter What If? mode
      setWhatIfMode(true);
      setModifiedCourses([...transcriptData.transcript.courses]);
      
      // Initialize whatIfResults with current GPA (no changes yet)
      const originalGPA = transcriptData?.transcript?.gpa_summary || {};
      setWhatIfResults({
        original: originalGPA,
        modified: originalGPA,
        changes: {
          weighted_change: 0,
          unweighted_change: 0,
          weighted_10_12_change: 0,
          cal_grant_change: 0
        }
      });
    }
    
  };

  // Get existing terms (both from transcript and future terms)
  const getExistingTerms = () => {
    const existingCourses = transcriptData?.transcript?.courses || [];
    const existingFutureTerms = futureTerms || [];
    
    const allExistingTerms = new Set();
    
    // Add terms from transcript
    existingCourses.forEach(course => {
      const grade = parseInt(course.Grade);
      const term = parseInt(course.Term);
      if (!isNaN(grade) && !isNaN(term)) {
        allExistingTerms.add(`${grade}-${term}`);
      }
    });
    
    // Add existing future terms
    existingFutureTerms.forEach(futureTerm => {
      const grade = parseInt(futureTerm.grade);
      const term = parseInt(futureTerm.term);
      allExistingTerms.add(`${grade}-${term}`);
    });
    
    return allExistingTerms;
  };

  // Add future term
  const addFutureTerm = (selectedGrade, selectedTerm) => {
    const newTerm = {
      id: Date.now(),
      grade: selectedGrade.toString().padStart(2, '0'),
      term: selectedTerm.toString(),
      courses: []
    };
    
    setFutureTerms(prev => [...prev, newTerm]);
  };

  // Add future course to a specific term
  const addFutureCourse = async (termId, courseData) => {
    // Infer course type from name if not explicitly provided
    const inferCourseType = (courseName, providedType) => {
      if (providedType && providedType !== 'Regular') return providedType;
      
      const nameUpper = courseName.toUpperCase();
      if (nameUpper.includes('AP ') || nameUpper.startsWith('AP')) return 'AP';
      if (nameUpper.includes('HONORS') || nameUpper.includes('HONOR')) return 'Honors';
      if (nameUpper.includes('IB ') || nameUpper.startsWith('IB')) return 'IB';
      return 'Regular';
    };
    
    const newCourse = {
      id: Date.now(),
      Course: courseData.name,
      Grade: futureTerms.find(t => t.id === termId)?.grade || '12',
      Term: futureTerms.find(t => t.id === termId)?.term || '1',
      Mark: courseData.expectedGrade || 'A',
      Credits: courseData.credits || 5,
      Department: courseData.department || '',
      // Treat community college as outside DVHS (always weighted),
      // and treat online/DVHS-style high school as DVHS (only weighted if Honors/AP via Type)
      Institution: courseData.department === 'Community College' ? 'Community College' : 'DVHS',
      Type: inferCourseType(courseData.name, courseData.type),
      isFuture: true
    };

    // Calculate updated course list immediately
    const updatedFutureCourses = [...futureCourses, newCourse];
    
    // Update state for UI
    setFutureCourses(updatedFutureCourses);
    
    // Update the term's courses
    setFutureTerms(prev => prev.map(term => 
      term.id === termId 
        ? { ...term, courses: [...term.courses, newCourse] }
        : term
    ));

    // Recalculate GPA immediately with the new course
    if (whatIfMode) {
      try {
        setCalculatingGPA(true);
        
        const combinedCourses = [...modifiedCourses, ...updatedFutureCourses];

        const response = await api.post('/gpa/what-if', {
          original_courses: transcriptData?.transcript?.courses || [],
          modified_courses: combinedCourses,
          original_gpa_summary: transcriptData?.transcript?.gpa_summary || {},
        });
        
        const data = response.data;
        
        // Update what-if results (normalize shape in case backend returns nested what_if_result)
        if (data.what_if_result) {
          const raw = data.what_if_result;
          const result = raw.what_if_result ? raw.what_if_result : raw;
          setWhatIfResults({
            original: result.original || {},
            modified: result.modified || {},
            changes: result.changes || {},
          });
        }
      } catch (error) {
        console.error('Error recalculating GPA after adding course:', error);
        alert('Failed to calculate GPA. Please check your connection and try again.');
      } finally {
        setCalculatingGPA(false);
      }
    }
  };

  // Remove future course
  const removeFutureCourse = async (courseId) => {
    // Calculate updated courses (without the removed course)
    const updatedFutureCourses = futureCourses.filter(course => course.id !== courseId);
    
    // Update state
    setFutureCourses(updatedFutureCourses);
    setFutureTerms(prev => prev.map(term => ({
      ...term,
      courses: term.courses.filter(course => course.id !== courseId)
    })));
    
    if (!whatIfMode) return;
    
    // If no future courses remain, reset to original GPA (no changes)
    if (updatedFutureCourses.length === 0) {
      const originalGPA = transcriptData?.transcript?.gpa_summary || {};
      setWhatIfResults({
        original: originalGPA,
        modified: originalGPA,
        changes: {
          weighted_change: 0,
          unweighted_change: 0,
          weighted_10_12_change: 0,
          cal_grant_change: 0
        }
      });
      return;
    }
    
    // Recalculate GPA with the updated courses
    try {
      setCalculatingGPA(true);
      
      const combinedCourses = [...modifiedCourses, ...updatedFutureCourses];
      
      const response = await api.post('/gpa/what-if', {
        original_courses: transcriptData?.transcript?.courses || [],
        modified_courses: combinedCourses,
        original_gpa_summary: transcriptData?.transcript?.gpa_summary  // Use official transcript GPAs
      });
      
      const data = response.data;
      
      if (data.what_if_result) {
        setWhatIfResults({
          original: data.what_if_result.original || {},
          modified: data.what_if_result.modified || {},
          changes: data.what_if_result.changes || {}
        });
      }
    } catch (error) {
      console.error('Error recalculating GPA after course removal:', error);
      alert('Failed to calculate GPA. Please check your connection and try again.');
    } finally {
      setCalculatingGPA(false);
    }
  };

  // Remove future term
  const removeFutureTerm = async (termId) => {
    // Find the term to remove
    const termToRemove = futureTerms.find(t => t.id === termId);
    if (!termToRemove) return;
    
    // Calculate updated courses (without the removed term's courses)
    const updatedFutureCourses = futureCourses.filter(course => 
      !(course.Grade === termToRemove.grade && course.Term === termToRemove.term)
    );
    
    // Update state
    setFutureCourses(updatedFutureCourses);
    setFutureTerms(prev => prev.filter(term => term.id !== termId));
    
    // Check if we removed any courses
    const termHadCourses = futureCourses.some(course => 
      course.Grade === termToRemove.grade && course.Term === termToRemove.term
    );
    
    if (!whatIfMode) return;
    
    // If no future courses remain, reset to original GPA (no changes)
    if (updatedFutureCourses.length === 0) {
      const originalGPA = transcriptData?.transcript?.gpa_summary || {};
      setWhatIfResults({
        original: originalGPA,
        modified: originalGPA,
        changes: {
          weighted_change: 0,
          unweighted_change: 0,
          weighted_10_12_change: 0,
          cal_grant_change: 0
        }
      });
      return;
    }
    
    // Only recalculate if the term had courses
    if (termHadCourses) {
      try {
        setCalculatingGPA(true);
        
        const combinedCourses = [...modifiedCourses, ...updatedFutureCourses];
        
        const response = await api.post('/gpa/what-if', {
          original_courses: transcriptData?.transcript?.courses || [],
          modified_courses: combinedCourses,
          original_gpa_summary: transcriptData?.transcript?.gpa_summary
        });
        
        const data = response.data;
        
        if (data.what_if_result) {
          setWhatIfResults({
            original: data.what_if_result.original || {},
            modified: data.what_if_result.modified || {},
            changes: data.what_if_result.changes || {}
          });
        }
      } catch (error) {
        console.error('Error recalculating GPA after term removal:', error);
        alert('Failed to calculate GPA. Please check your connection and try again.');
      } finally {
        setCalculatingGPA(false);
      }
    }
  };

  // Recalculate GPA including future courses
  const recalculateGPAWithFutureCourses = async () => {
    if (!whatIfMode) return;
    
    try {
      setCalculatingGPA(true);
      
      const combinedCourses = [...modifiedCourses, ...futureCourses];





      const response = await api.post('/gpa/what-if', {
        original_courses: transcriptData?.transcript?.courses || [],
        modified_courses: combinedCourses,
        original_gpa_summary: transcriptData?.transcript?.gpa_summary
      });
      
      const data = response.data;
      
      if (data.what_if_result) {
        setWhatIfResults({
          original: data.what_if_result.original || {},
          modified: data.what_if_result.modified || {},
          changes: data.what_if_result.changes || {}
        });
      }
    } catch (error) {
      console.error('Error calculating GPA with future courses:', error);
      alert('Failed to calculate GPA. Please check your connection and try again.');
    } finally {
      setCalculatingGPA(false);
    }
  };

  // Handle grade change for future courses
  const handleFutureCourseGradeChange = async (courseId, newGrade) => {
    // Calculate updated courses BEFORE setting state
    const updatedFutureCourses = futureCourses.map(course => 
      course.id === courseId 
        ? { ...course, Mark: newGrade }
        : course
    );
    
    // Update state for UI
    setFutureCourses(updatedFutureCourses);
    
    // Update the term's courses as well
    setFutureTerms(prev => prev.map(term => ({
      ...term,
      courses: term.courses.map(course => 
        course.id === courseId 
          ? { ...course, Mark: newGrade }
          : course
      )
    })));

    // Recalculate GPA immediately with updated courses
    if (whatIfMode) {
      try {
        setCalculatingGPA(true);
        
        const combinedCourses = [...modifiedCourses, ...updatedFutureCourses];
        
        const response = await api.post('/gpa/what-if', {
          original_courses: transcriptData?.transcript?.courses || [],
          modified_courses: combinedCourses,
          original_gpa_summary: transcriptData?.transcript?.gpa_summary
        });
        
        const data = response.data;
        
        if (data.what_if_result) {
          setWhatIfResults({
            original: data.what_if_result.original || {},
            modified: data.what_if_result.modified || {},
            changes: data.what_if_result.changes || {}
          });
        }
      } catch (error) {
        console.error('Error recalculating GPA after grade change:', error);
        alert('Failed to calculate GPA. Please check your connection and try again.');
      } finally {
        setCalculatingGPA(false);
      }
    }
  };

  // Drag and drop handlers for all courses
  const handleCourseDragStart = (e, course, termKey, isFuture) => {




    setDraggedCourse({ 
      course, 
      sourceTermKey: termKey,
      isFuture: isFuture || false
    });
    e.dataTransfer.effectAllowed = 'move';
    
    // Create a custom drag image
    const dragImage = document.createElement('div');
    dragImage.className = 'fixed z-[10000] bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-2xl font-medium';
    dragImage.textContent = course.Course;
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
    
    // Make the original row semi-transparent
    e.currentTarget.style.opacity = '0.4';
  };

  const handleCourseDragOver = (e, index, termKey) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    
    // Update drag preview position
    if (!dragPreviewPosition || dragPreviewPosition.index !== index || dragPreviewPosition.termKey !== termKey) {

      setDragPreviewPosition({ index, termKey });
    }
  };

  const handleCourseDragLeave = (e) => {
    // Don't clear on drag leave - let drop or drag end handle it
    // This prevents flickering of the indicator
  };

  const handleCourseDrop = async (e, dropIndex, dropTermKey) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.style.opacity = '1';



    if (!draggedCourse) {

      return;
    }
    
    const { course: droppedCourse, sourceTermKey, isFuture: isSourceFuture } = draggedCourse;



    // Only allow drag within the same term
    if (sourceTermKey !== dropTermKey) {

      setDraggedCourse(null);
      setDragOverIndex(null);
      setDragOverTerm(null);
      return;
    }
    
    // Only allow reordering within What-If terms (no GPA recalculation needed)
    if (!isSourceFuture) {

      setDraggedCourse(null);
      setDragOverIndex(null);
      setDragOverTerm(null);
      return;
    }
    
    // Find the What-If term by parsing the key
    // Key format: "12|1|Dougherty Valley High School"
    const [gradeNum, termNum] = sourceTermKey.split('|');


    const futureTerm = futureTerms.find(t => {

      return t.grade === gradeNum && t.term === termNum;
    });

    if (!futureTerm) {

      setDraggedCourse(null);
      setDragOverIndex(null);
      setDragOverTerm(null);
      return;
    }
    
    try {
      // Reorder courses within the What-If term (no GPA change)
      const newFutureTerms = futureTerms.map(term => {
        if (term.id === futureTerm.id) {
          const newCourses = term.courses.filter(c => c.id !== droppedCourse.id);
          newCourses.splice(dropIndex, 0, droppedCourse);
          return { ...term, courses: newCourses };
        }
        return term;
      });
      
      setFutureTerms(newFutureTerms);
      setFutureCourses(newFutureTerms.flatMap(t => t.courses));
    } catch (error) {
      console.error('Error reordering course:', error);
    } finally {
      setDraggedCourse(null);
      setDragOverIndex(null);
      setDragOverTerm(null);
      setDragPreviewPosition(null);
    }
  };

  const handleCourseDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedCourse(null);
    setDragOverIndex(null);
    setDragOverTerm(null);
    setDragPreviewPosition(null);
  };

  const handleGradeChange = async (courseIndex, newGrade) => {
    if (!whatIfMode) return;





    // Set calculating state to show loading indicator
    setCalculatingGPA(true);
    
    try {
      // Create a deep copy of the modified courses array
      const updatedCourses = JSON.parse(JSON.stringify(modifiedCourses));



      // Find the course in the original transcript to get its original state
      const originalCourse = transcriptData.transcript.courses.find(c => 
        c.Course === updatedCourses[courseIndex].Course && 
        c.Term === updatedCourses[courseIndex].Term
      );
      
      if (!originalCourse) {
        console.error('Original course not found!');
        return; // Safety check
      }
      
      // Update the grade for the specified course
      updatedCourses[courseIndex] = {
        ...updatedCourses[courseIndex],
        Mark: newGrade,
        // Preserve the original grade for reference
        originalMark: originalCourse.Mark
      };

      // Update the local state immediately for better UX
      setModifiedCourses(updatedCourses);
      
      // Call the backend to calculate the new GPA with the updated courses
      try {
        const response = await api.post('/gpa/what-if', {
          original_courses: transcriptData.transcript.courses,
          modified_courses: [...updatedCourses, ...futureCourses],
          original_gpa_summary: transcriptData.transcript.gpa_summary
        });
        
        const data = response.data;
        
        if (data.what_if_result) {
          const newResults = {
            original: data.what_if_result.original || {},
            modified: data.what_if_result.modified || {},
            changes: data.what_if_result.changes || {}
          };

          setWhatIfResults(newResults);
          // Force a re-render by updating the state
          setCalculatingGPA(false);
        } else {
          throw new Error('Invalid response format from server');
        }
      } catch (apiError) {
        console.error('API Error:', apiError);
        setCalculatingGPA(false);
        alert('Failed to calculate GPA. Please check your connection and try again.');
      }
      
    } catch (error) {
      console.error('Error in handleGradeChange:', error);
    }
  };
  

  // Toggle term collapse state
  const toggleTermCollapse = (termKey) => {
    setCollapsedTerms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(termKey)) {
        newSet.delete(termKey);
      } else {
        newSet.add(termKey);
      }
      return newSet;
    });
  };

  // Collapse all terms
  const collapseAllTerms = () => {
    const coursesToShow = whatIfMode ? [...modifiedCourses, ...futureCourses] : transcript.courses;
    const groupedCourses = {};
    
    coursesToShow.forEach(course => {
      // Do not let future/custom What-If courses affect transcript term grouping
      if (whatIfMode && course.isFuture) return;
      const grade = course.Grade || 'Unknown';
      const term = course.Term || 'Unknown';
      const institution = course.Institution || 'Dougherty Valley High School';
      const key = `${grade}|${term}|${institution}`;
      groupedCourses[key] = true;
    });
    
    // Add dedicated What-If term groups in What-If mode
    if (whatIfMode) {
      futureTerms.forEach(futureTerm => {
        const key = `${futureTerm.grade}|${futureTerm.term}|What If`;
        groupedCourses[key] = true;
      });
    }
    
    setCollapsedTerms(new Set(Object.keys(groupedCourses)));
  };

  // Expand all terms
  const expandAllTerms = () => {
    setCollapsedTerms(new Set());
  };

  // Check if all terms are collapsed
  const areAllTermsCollapsed = () => {
    const coursesToShow = whatIfMode ? [...modifiedCourses, ...futureCourses] : transcript.courses;
    const groupedCourses = {};
    
    coursesToShow.forEach(course => {
      // Ignore future/custom What-If courses here; they belong to dedicated What-If groups
      if (whatIfMode && course.isFuture) return;
      const grade = course.Grade || 'Unknown';
      const term = course.Term || 'Unknown';
      const institution = course.Institution || 'Dougherty Valley High School';
      const key = `${grade}|${term}|${institution}`;
      groupedCourses[key] = true;
    });
    
    // Add dedicated What-If term groups in What-If mode
    if (whatIfMode) {
      futureTerms.forEach(futureTerm => {
        const key = `${futureTerm.grade}|${futureTerm.term}|What If`;
        groupedCourses[key] = true;
      });
    }
    
    const totalTerms = Object.keys(groupedCourses).length;
    return totalTerms > 0 && collapsedTerms.size === totalTerms;
  };

  // Reset to original grades
  const resetToOriginal = async () => {
    const originalCourses = [...transcriptData.transcript.courses];
    setModifiedCourses(originalCourses);
    
    // Clear all future terms and courses
    setFutureCourses([]);
    setFutureTerms([]);
    
    // Reset What-If results to show original GPA with no changes
    const originalGPA = transcriptData?.transcript?.gpa_summary || {};
    setWhatIfResults({
      original: originalGPA,
      modified: originalGPA,  // Same as original since we're resetting
      changes: {
        weighted_change: 0,
        unweighted_change: 0,
        weighted_10_12_change: 0,
        cal_grant_change: 0
      }
    });
  };

  // Fetch transcript images
  const fetchTranscriptImages = async () => {
    // Check if we have transcript data at all
    if (!transcriptData) {
      alert('No transcript data available');
      return;
    }

    setLoadingImages(true);
    try {
      const response = await fetch('/transcript-images', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          transcript_file_key: "request_images"
        })
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('Response error:', errorText);
        throw new Error(`Server error: ${errorText}`);
      }

      const data = await response.json();
      setTranscriptImages(data.images || []);
      setShowTranscriptImages(true);
    } catch (error) {
      console.error('Error fetching transcript images:', error);
      alert(`Failed to load transcript images: ${error.message}. Check console and backend logs for details.`);
    } finally {
      setLoadingImages(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading transcript data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isNoTranscript = error.includes('No transcript data found') || error.includes('No active transcript');
    
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-8 text-center">
          {isNoTranscript ? (
            <>
              <svg className="h-24 w-24 text-gray-400 dark:text-gray-600 mx-auto mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">No Transcript Found</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                You haven't uploaded a transcript yet. Upload your transcript in Settings to view your courses and GPA.
              </p>
              <button
                onClick={() => {
                  // Dispatch event to open settings and switch to transcript section
                  const event = new CustomEvent('openSettings', { detail: 'transcript' });
                  window.dispatchEvent(event);
                }}
                className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md hover:shadow-lg"
              >
                Go to Settings
              </button>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">Error</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const transcript = transcriptData.transcript;
  const transcriptFilePath = transcriptData.transcript_file_path;
  const transcriptFileKey = transcriptData.transcript_file_key;
  const transcriptFileUrl = transcriptData.transcript_file_url;
  
  // Determine file URL - prefer S3 URL if available, otherwise construct local URL
  let fileUrl = transcriptFileUrl;
  if (!fileUrl && transcriptFilePath) {
    const uploadsIndex = transcriptFilePath.lastIndexOf('/uploads/');
    if (uploadsIndex !== -1) {
      fileUrl = `${API.replace('/api','')}/uploads/${transcriptFilePath.substring(uploadsIndex + 9)}`;
    }
  }

      if (!transcript) {
      return (
        <div className="space-y-6">
          {fileUrl && (
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-4 border-l-4 border-indigo-500 flex items-center space-x-4">
              <DocumentTextIcon className="h-8 w-8 text-indigo-600" />
              <div>
                <div className="font-semibold text-gray-900">Uploaded Transcript File</div>
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline text-sm"
                >
                  View or Download Transcript
                </a>
              </div>
            </div>
          )}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Transcript Not Yet Processed</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">Your transcript is being processed. Please refresh this page in a moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Student Info Summary */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-800">
        <div className="border-b border-gray-200 dark:border-gray-800 pb-3 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Student Information</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="backdrop-blur-sm p-6 rounded-2xl border-[1px] [border-color:color-mix(in_srgb,var(--accent-200)_50%,transparent)] dark:[border-color:color-mix(in_srgb,var(--accent-500)_20%,transparent)] transition-all duration-300 hover:shadow-lg [background:linear-gradient(to_bottom_right,var(--accent-50),color-mix(in_srgb,var(--accent-100)_50%,transparent))] dark:[background:linear-gradient(to_bottom_right,color-mix(in_srgb,var(--accent-900)_20%,transparent),color-mix(in_srgb,var(--accent-800)_10%,transparent))]" onMouseEnter={(e) => {
            e.currentTarget.style.borderWidth = '2px';
            e.currentTarget.style.borderColor = 'var(--accent-300)';
            e.currentTarget.style.boxShadow = '0 10px 15px -3px color-mix(in srgb, var(--accent-500) 20%, transparent), 0 4px 6px -4px color-mix(in srgb, var(--accent-500) 20%, transparent)';
          }} onMouseLeave={(e) => {
            const isDark = document.documentElement.classList.contains('dark');
            e.currentTarget.style.borderWidth = '1px';
            e.currentTarget.style.borderColor = isDark ? 'color-mix(in srgb, var(--accent-500) 20%, transparent)' : 'color-mix(in srgb, var(--accent-200) 50%, transparent)';
            e.currentTarget.style.boxShadow = '';
          }}>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium mb-2">Student ID</h3>
            <p className="text-2xl sm:text-3xl font-bold [color:var(--accent-600)] dark:[color:var(--accent-400)] pointer-events-none break-words">
              {transcript.student_info?.student_id || transcript.student_info?.student_number || 'Not Found'}
            </p>
          </div>
          <div className="backdrop-blur-sm p-6 rounded-2xl border-[1px] [border-color:color-mix(in_srgb,rgb(187_247_208)_50%,transparent)] dark:[border-color:color-mix(in_srgb,rgb(34_197_94)_20%,transparent)] transition-all duration-300 hover:shadow-lg [background:linear-gradient(to_bottom_right,rgb(240_253_244),color-mix(in_srgb,rgb(220_252_231)_50%,transparent))] dark:[background:linear-gradient(to_bottom_right,color-mix(in_srgb,rgb(20_83_45)_20%,transparent),color-mix(in_srgb,rgb(22_101_52)_10%,transparent))]" onMouseEnter={(e) => {
            e.currentTarget.style.borderWidth = '2px';
            e.currentTarget.style.borderColor = 'rgb(134, 239, 172)';
            e.currentTarget.style.boxShadow = '0 10px 15px -3px color-mix(in srgb, rgb(34, 197, 94) 20%, transparent), 0 4px 6px -4px color-mix(in srgb, rgb(34, 197, 94) 20%, transparent)';
          }} onMouseLeave={(e) => {
            const isDark = document.documentElement.classList.contains('dark');
            e.currentTarget.style.borderWidth = '1px';
            e.currentTarget.style.borderColor = isDark ? 'color-mix(in srgb, rgb(34, 197, 94) 20%, transparent)' : 'color-mix(in srgb, rgb(187, 247, 208) 50%, transparent)';
            e.currentTarget.style.boxShadow = '';
          }}>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium mb-2">Grade Level</h3>
            <p className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400 pointer-events-none">
              {transcript.student_info?.grade_level || transcript.student_info?.grade || 'Not Available'}
            </p>
          </div>
          <div className="backdrop-blur-sm p-6 rounded-2xl border-[1px] [border-color:color-mix(in_srgb,var(--accent-200)_50%,transparent)] dark:[border-color:color-mix(in_srgb,var(--accent-500)_20%,transparent)] transition-all duration-300 hover:shadow-lg [background:linear-gradient(to_bottom_right,var(--accent-50),color-mix(in_srgb,var(--accent-100)_50%,transparent))] dark:[background:linear-gradient(to_bottom_right,color-mix(in_srgb,var(--accent-900)_20%,transparent),color-mix(in_srgb,var(--accent-800)_10%,transparent))]" onMouseEnter={(e) => {
            e.currentTarget.style.borderWidth = '2px';
            e.currentTarget.style.borderColor = 'var(--accent-300)';
            e.currentTarget.style.boxShadow = '0 10px 15px -3px color-mix(in srgb, var(--accent-500) 20%, transparent), 0 4px 6px -4px color-mix(in srgb, var(--accent-500) 20%, transparent)';
          }} onMouseLeave={(e) => {
            const isDark = document.documentElement.classList.contains('dark');
            e.currentTarget.style.borderWidth = '1px';
            e.currentTarget.style.borderColor = isDark ? 'color-mix(in srgb, var(--accent-500) 20%, transparent)' : 'color-mix(in srgb, var(--accent-200) 50%, transparent)';
            e.currentTarget.style.boxShadow = '';
          }}>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium mb-2">Student Name</h3>
            <p className="text-2xl sm:text-3xl font-bold [color:var(--accent-600)] dark:[color:var(--accent-400)] break-words">
              {transcript.student_info?.name || 'Not Found'}
            </p>
          </div>
          <div className="backdrop-blur-sm p-6 rounded-2xl border-[1px] border-gray-300 dark:border-gray-700 transition-all duration-300 hover:shadow-lg bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-900/50" onMouseEnter={(e) => {
            e.currentTarget.style.borderWidth = '2px';
            e.currentTarget.style.borderColor = 'var(--accent-400)';
            e.currentTarget.style.boxShadow = '0 10px 15px -3px color-mix(in srgb, var(--accent-500) 20%, transparent), 0 4px 6px -4px color-mix(in srgb, var(--accent-500) 20%, transparent)';
            e.currentTarget.style.transform = 'none';
          }} onMouseLeave={(e) => {
            e.currentTarget.style.borderWidth = '1px';
            e.currentTarget.style.borderColor = document.documentElement.classList.contains('dark') ? 'rgb(55, 65, 81)' : 'rgb(209, 213, 219)';
            e.currentTarget.style.boxShadow = '';
            e.currentTarget.style.transform = 'none';
          }}>
            <div className="mb-2">
              <h3 className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium">Schools Attended</h3>
            </div>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {transcriptData.schools && transcriptData.schools.length > 0 ? (
                <div className="space-y-1 max-h-32 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                  {transcriptData.schools.map((school, idx) => (
                    <div key={idx} className="flex items-start gap-1">
                      <span className="text-[var(--accent-500)] mt-0.5">•</span>
                      <span className="break-words">{school}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span>DVHS</span>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-normal">
                Based on transcript
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* GPA Summary */}
      <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 border transition-all duration-300 ${
        whatIfMode 
          ? 'border-purple-500 dark:border-purple-500/60 ring-2 ring-purple-500/40 dark:ring-purple-500/30 shadow-[0_0_20px_rgba(168,85,247,0.15)]' 
          : 'border-gray-200 dark:border-gray-800'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div className="border-b border-gray-200 dark:border-gray-800 pb-3 sm:flex-1 sm:mr-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">GPA Summary</h2>
              <HelpTooltip 
                content="If the displayed GPAs are incorrect, delete your transcript in Settings and re-upload it to recalculate."
                position="right"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2">
            <button
              onClick={toggleWhatIfMode}
              className={`px-4 sm:px-6 py-3 text-sm sm:text-base rounded-xl font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                whatIfMode
                  ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40'
                  : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40 sm:hover:scale-105'
              }`}
            >
              {whatIfMode ? '✕ Exit What If?' : '🔮 What If?'}
            </button>
            <HelpTooltip 
              content="Test how future grades will affect your GPA without changing your transcript. Click on any grade to edit it!"
              position="left"
            />
          </div>
        </div>
        
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-6">
          {gpaVisibility.weighted && (
          <div className="backdrop-blur-sm p-6 rounded-2xl border-[1px] [border-color:color-mix(in_srgb,rgb(147_197_253)_50%,transparent)] dark:[border-color:color-mix(in_srgb,rgb(59_130_246)_20%,transparent)] transition-all duration-300 hover:shadow-lg [background:linear-gradient(to_bottom_right,rgb(239_246_255),color-mix(in_srgb,rgb(219_234_254)_50%,transparent))] dark:[background:linear-gradient(to_bottom_right,color-mix(in_srgb,rgb(30_58_138)_20%,transparent),color-mix(in_srgb,rgb(30_64_175)_10%,transparent))]" onMouseEnter={(e) => {
            e.currentTarget.style.borderWidth = '2px';
            e.currentTarget.style.borderColor = 'rgb(147, 197, 253)';
            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(59, 130, 246, 0.3), 0 4px 6px -4px rgba(59, 130, 246, 0.3)';
            e.currentTarget.style.transform = 'none';
          }} onMouseLeave={(e) => {
            const isDark = document.documentElement.classList.contains('dark');
            e.currentTarget.style.borderWidth = '1px';
            e.currentTarget.style.borderColor = isDark ? 'color-mix(in srgb, rgb(59, 130, 246) 20%, transparent)' : 'color-mix(in srgb, rgb(147, 197, 253) 50%, transparent)';
            e.currentTarget.style.boxShadow = '';
            e.currentTarget.style.transform = 'none';
          }}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium">Weighted GPA</h3>
              <GPAInfoTooltip type="weighted" />
            </div>
            <div className="flex items-baseline space-x-2">
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 pointer-events-none">
                {(() => {
                  if (whatIfMode && whatIfResults && whatIfResults.modified) {
                    const modifiedGPA = (whatIfResults.modified.cumulative_weighted_gpa ?? whatIfResults.modified.cumulative_gpa_weighted ?? 0).toFixed(2);

                    return modifiedGPA;
                  } else {
                    return displayGPA(transcript.gpa_summary?.cumulative_weighted_gpa || transcript.gpa_summary?.cumulative_gpa_weighted) || '0.00';
                  }
                })()}
              </p>
              {whatIfMode && whatIfResults && whatIfResults.modified && whatIfResults.original && (() => {
                // Calculate visual change from displayed rounded values
                const modifiedWeighted = whatIfResults.modified.cumulative_weighted_gpa ?? whatIfResults.modified.cumulative_gpa_weighted ?? 0;
                const originalWeighted = whatIfResults.original.cumulative_weighted_gpa ?? whatIfResults.original.cumulative_gpa_weighted ?? 0;
                const displayedModified = parseFloat(modifiedWeighted.toFixed(2));
                const displayedOriginal = parseFloat(originalWeighted.toFixed(2));
                const visualChange = displayedModified - displayedOriginal;
                return Math.abs(visualChange) >= 0.01 ? (
                  <span className={`flex items-center space-x-0.5 text-sm font-bold px-2 py-1 rounded-full ${
                    visualChange > 0 
                      ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30' 
                      : 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
                  }`}>
                    {visualChange > 0 ? (
                      <><ArrowUpIcon className="h-3 w-3" /><span>+{visualChange.toFixed(2)}</span></>
                    ) : (
                      <><ArrowDownIcon className="h-3 w-3" /><span>{visualChange.toFixed(2)}</span></>
                    )}
                  </span>
                ) : null;
              })()}
            </div>
          </div>
          )}
          {gpaVisibility.unweighted && (
          <div className="backdrop-blur-sm p-6 rounded-2xl border-[1px] [border-color:color-mix(in_srgb,rgb(187_247_208)_50%,transparent)] dark:[border-color:color-mix(in_srgb,rgb(34_197_94)_20%,transparent)] transition-all duration-300 hover:shadow-lg [background:linear-gradient(to_bottom_right,rgb(240_253_244),color-mix(in_srgb,rgb(220_252_231)_50%,transparent))] dark:[background:linear-gradient(to_bottom_right,color-mix(in_srgb,rgb(20_83_45)_20%,transparent),color-mix(in_srgb,rgb(22_101_52)_10%,transparent))]" onMouseEnter={(e) => {
            e.currentTarget.style.borderWidth = '2px';
            e.currentTarget.style.borderColor = 'rgb(134, 239, 172)';
            e.currentTarget.style.boxShadow = '0 10px 15px -3px color-mix(in srgb, rgb(34, 197, 94) 20%, transparent), 0 4px 6px -4px color-mix(in srgb, rgb(34, 197, 94) 20%, transparent)';
            e.currentTarget.style.transform = 'none';
          }} onMouseLeave={(e) => {
            const isDark = document.documentElement.classList.contains('dark');
            e.currentTarget.style.borderWidth = '1px';
            e.currentTarget.style.borderColor = isDark ? 'color-mix(in srgb, rgb(34, 197, 94) 20%, transparent)' : 'color-mix(in srgb, rgb(187, 247, 208) 50%, transparent)';
            e.currentTarget.style.boxShadow = '';
            e.currentTarget.style.transform = 'none';
          }}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium">Unweighted GPA</h3>
              <GPAInfoTooltip type="unweighted" />
            </div>
            <div className="flex items-baseline space-x-2">
              <p className="text-3xl font-bold text-green-600 dark:text-green-400 pointer-events-none">
                {whatIfMode && whatIfResults && whatIfResults.modified
                  ? (whatIfResults.modified.cumulative_unweighted_gpa ?? whatIfResults.modified.cumulative_gpa_unweighted ?? whatIfResults.modified.unweighted_gpa ?? 0).toFixed(2)
                  : displayGPA(transcript.gpa_summary?.cumulative_unweighted_gpa || transcript.gpa_summary?.cumulative_gpa_unweighted) || '0.00'
                }
              </p>
              {whatIfMode && whatIfResults && whatIfResults.modified && whatIfResults.original && (() => {
                // Calculate visual change from displayed rounded values
                const modifiedUnweighted = whatIfResults.modified.cumulative_unweighted_gpa ?? whatIfResults.modified.cumulative_gpa_unweighted ?? whatIfResults.modified.unweighted_gpa ?? 0;
                const originalUnweighted = whatIfResults.original.cumulative_unweighted_gpa ?? whatIfResults.original.cumulative_gpa_unweighted ?? whatIfResults.original.unweighted_gpa ?? 0;
                const displayedModified = parseFloat(modifiedUnweighted.toFixed(2));
                const displayedOriginal = parseFloat(originalUnweighted.toFixed(2));
                const visualChange = displayedModified - displayedOriginal;
                return Math.abs(visualChange) >= 0.01 ? (
                  <span className={`flex items-center space-x-0.5 text-sm font-bold px-2 py-1 rounded-full ${
                    visualChange > 0 
                      ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30' 
                      : 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
                  }`}>
                    {visualChange > 0 ? (
                      <><ArrowUpIcon className="h-3 w-3" /><span>+{visualChange.toFixed(2)}</span></>
                    ) : (
                      <><ArrowDownIcon className="h-3 w-3" /><span>{visualChange.toFixed(2)}</span></>
                    )}
                  </span>
                ) : null;
              })()}
            </div>
          </div>
          )}
          {gpaVisibility.tenTwelve && (
          <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 dark:from-teal-900/20 dark:to-teal-800/10 backdrop-blur-sm p-6 rounded-2xl border-[1px] border-teal-200/50 dark:border-teal-500/20 transition-all duration-300 hover:shadow-lg" onMouseEnter={(e) => {
            e.currentTarget.style.borderWidth = '2px';
            e.currentTarget.style.borderColor = 'rgb(94 234 212)';
            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(20, 184, 166, 0.3), 0 4px 6px -4px rgba(20, 184, 166, 0.3)';
            e.currentTarget.style.transform = 'none';
          }} onMouseLeave={(e) => {
            const isDark = document.documentElement.classList.contains('dark');
            e.currentTarget.style.borderWidth = '1px';
            e.currentTarget.style.borderColor = isDark ? 'rgba(20, 184, 166, 0.2)' : 'rgba(153, 246, 228, 0.5)';
            e.currentTarget.style.boxShadow = '';
            e.currentTarget.style.transform = 'none';
          }}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium">10-12 Weighted</h3>
              <GPAInfoTooltip type="weighted_10_12" />
            </div>
            <div className="flex items-baseline space-x-2">
              <p className="text-3xl font-bold text-teal-600 dark:text-teal-400">
                {whatIfMode && whatIfResults && whatIfResults.modified
                  ? (whatIfResults.modified.weighted_10_12_ag_gpa ?? 0).toFixed(2)
                  : displayGPA(transcript.gpa_summary?.weighted_10_12_ag_gpa) || '0.00'
                }
              </p>
              {whatIfMode && whatIfResults && whatIfResults.modified && whatIfResults.original && (() => {
                // Calculate visual change from displayed rounded values
                const modifiedTenTwelve = whatIfResults.modified.weighted_10_12_ag_gpa ?? 0;
                const originalTenTwelve = whatIfResults.original.weighted_10_12_ag_gpa ?? 0;
                const displayedModified = parseFloat(modifiedTenTwelve.toFixed(2));
                const displayedOriginal = parseFloat(originalTenTwelve.toFixed(2));
                const visualChange = displayedModified - displayedOriginal;
                return Math.abs(visualChange) >= 0.01 ? (
                  <span className={`flex items-center space-x-0.5 text-sm font-bold px-2 py-1 rounded-full ${
                    visualChange > 0 
                      ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30' 
                      : 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
                  }`}>
                    {visualChange > 0 ? (
                      <><ArrowUpIcon className="h-3 w-3" /><span>+{visualChange.toFixed(2)}</span></>
                    ) : (
                      <><ArrowDownIcon className="h-3 w-3" /><span>{visualChange.toFixed(2)}</span></>
                    )}
                  </span>
                ) : null;
              })()}
            </div>
          </div>
          )}
          {gpaVisibility.calGrant && (
          <div className="bg-gradient-to-br from-pink-50 to-pink-100/50 dark:from-pink-900/20 dark:to-pink-800/10 backdrop-blur-sm p-6 rounded-2xl border-[1px] border-pink-200/50 dark:border-pink-500/20 transition-all duration-300 hover:shadow-lg hover:shadow-pink-500/20" onMouseEnter={(e) => {
            e.currentTarget.style.borderWidth = '2px';
            e.currentTarget.style.borderColor = 'rgb(249 168 212)';
            e.currentTarget.style.transform = 'none';
          }} onMouseLeave={(e) => {
            e.currentTarget.style.borderWidth = '1px';
            const isDark = document.documentElement.classList.contains('dark');
            e.currentTarget.style.borderColor = isDark ? 'rgba(236, 72, 153, 0.2)' : 'rgba(251, 207, 232, 0.5)';
            e.currentTarget.style.transform = 'none';
          }}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium">Cal Grant</h3>
              <GPAInfoTooltip type="cal_grant" />
            </div>
            <div className="flex items-baseline space-x-2">
              <p className="text-3xl font-bold text-pink-600 dark:text-pink-400">
                {whatIfMode && whatIfResults && whatIfResults.modified
                  ? (whatIfResults.modified.cal_grant_gpa ?? whatIfResults.modified.ca_cal_grant_gpa ?? 0).toFixed(2)
                  : displayGPA(transcript.gpa_summary?.cal_grant_gpa || transcript.gpa_summary?.ca_cal_grant_gpa) || '0.00'
                }
              </p>
              {whatIfMode && whatIfResults && whatIfResults.modified && whatIfResults.original && (() => {
                // Calculate visual change from displayed rounded values
                const modifiedCalGrant = whatIfResults.modified.cal_grant_gpa ?? whatIfResults.modified.ca_cal_grant_gpa ?? 0;
                const originalCalGrant = whatIfResults.original.cal_grant_gpa ?? whatIfResults.original.ca_cal_grant_gpa ?? 0;
                const displayedModified = parseFloat(modifiedCalGrant.toFixed(2));
                const displayedOriginal = parseFloat(originalCalGrant.toFixed(2));
                const visualChange = displayedModified - displayedOriginal;
                return Math.abs(visualChange) >= 0.01 ? (
                  <span className={`flex items-center space-x-0.5 text-sm font-bold px-2 py-1 rounded-full ${
                    visualChange > 0 
                      ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30' 
                      : 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
                  }`}>
                    {visualChange > 0 ? (
                      <><ArrowUpIcon className="h-3 w-3" /><span>+{visualChange.toFixed(2)}</span></>
                    ) : (
                      <><ArrowDownIcon className="h-3 w-3" /><span>{visualChange.toFixed(2)}</span></>
                    )}
                  </span>
                ) : null;
              })()}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Credit Summary */}
      {transcript.credit_summary && Object.keys(transcript.credit_summary).length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-800">
          <div className="border-b border-gray-200 dark:border-gray-800 pb-3 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Credit Summary</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 dark:from-indigo-900/20 dark:to-indigo-800/10 backdrop-blur-sm p-5 rounded-2xl border border-indigo-200/50 dark:border-indigo-500/20 hover:border-indigo-300 dark:hover:border-indigo-500/40 transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/20">
              <h3 className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium mb-2">Attempted</h3>
              <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                {transcript.credit_summary.attempted}
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10 backdrop-blur-sm p-5 rounded-2xl border border-green-200/50 dark:border-green-500/20 hover:border-green-300 dark:hover:border-green-500/40 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20">
              <h3 className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium mb-2">Earned</h3>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                {transcript.credit_summary.earned}
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10 backdrop-blur-sm p-5 rounded-2xl border border-blue-200/50 dark:border-blue-500/20 hover:border-blue-300 dark:hover:border-blue-500/40 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20">
              <h3 className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium mb-2">Required</h3>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {transcript.credit_summary.required}
              </p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-800/10 backdrop-blur-sm p-5 rounded-2xl border border-orange-200/50 dark:border-orange-500/20 hover:border-orange-300 dark:hover:border-orange-500/40 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/20">
              <h3 className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium mb-2">Remaining</h3>
              <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                {transcript.credit_summary.remaining}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Course History by Year/Term */}
      {(transcript.courses && transcript.courses.length > 0) && (
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between mb-6">
            <div className="border-b border-gray-200 dark:border-gray-800 pb-3 flex-1 mr-4">
              <div className="flex items-center space-x-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Course History</h2>
                <HelpTooltip 
                  content="If a course is missing from your transcript, go to Profile & Settings → Transcript Management to add it manually."
                  position="right"
                />
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {whatIfMode && (
                <>
                  <button
                    onClick={resetToOriginal}
                    className="px-4 py-2.5 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-white dark:hover:text-white border-2 border-gray-600 dark:border-gray-500 rounded-xl hover:bg-gray-600 dark:hover:bg-gray-500 transition-all duration-300 flex items-center space-x-2 shadow-md hover:shadow-lg hover:scale-105"
                  >
                    <span>🔄</span>
                    <span>Reset to Original</span>
                  </button>
                  <FutureTermSelector 
                    existingTerms={getExistingTerms()}
                    onAddTerm={addFutureTerm}
                  />
                </>
              )}
              <button
                onClick={() => {
                  if (areAllTermsCollapsed()) {
                    expandAllTerms();
                  } else {
                    collapseAllTerms();
                  }
                }}
                className="px-4 py-2.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-white dark:hover:text-white border-2 border-indigo-600 dark:border-indigo-500 rounded-xl hover:bg-indigo-600 dark:hover:bg-indigo-500 transition-all duration-300 flex items-center space-x-2 shadow-md hover:shadow-lg hover:scale-105"
              >
                {areAllTermsCollapsed() ? (
                  <>
                    <ChevronDownIcon className="w-4 h-4 transition-transform duration-300" />
                    <span>Expand All</span>
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="w-4 h-4 transition-transform duration-300 rotate-180" />
                    <span>Collapse All</span>
                  </>
                )}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700" style={{ width: '60%' }}>
                    Course Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700" style={{ width: '15%' }}>
                    Credits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700" style={{ width: '25%' }}>
                    Grade
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {(() => {
                  const coursesToShow = whatIfMode ? [...modifiedCourses, ...futureCourses] : transcript.courses;
                  const groupedCourses = {};
                  
                  // Group transcript/modified courses by Grade, Term, and Institution
                  coursesToShow.forEach(course => {
                    // Keep future/custom What-If courses out of transcript term groups
                    if (whatIfMode && course.isFuture) return;
                    const grade = course.Grade || 'Unknown';
                    const term = course.Term || 'Unknown';
                    let institution = course.Institution || 'Dougherty Valley High School';
                    
                    // Normalize DVHS variants for consistent grouping
                    if (institution.toLowerCase().includes('dougherty valley') || institution.toLowerCase() === 'dvhs') {
                      institution = 'DVHS';
                    }
                    
                    const key = `${grade}|${term}|${institution}`;
                    if (!groupedCourses[key]) {
                      groupedCourses[key] = [];
                    }
                    groupedCourses[key].push(course);
                  });

                  // Add dedicated What-If term groups (with their own courses)
                  if (whatIfMode) {
                    futureTerms.forEach(futureTerm => {
                      const key = `${futureTerm.grade}|${futureTerm.term}|What If`;
                      if (!groupedCourses[key]) {
                        groupedCourses[key] = [];
                      }
                      // Attach this term's future courses to the What-If group
                      groupedCourses[key].push(...futureTerm.courses);
                      // Mark this as a future term
                      groupedCourses[key].isFutureTerm = true;
                      groupedCourses[key].futureTermId = futureTerm.id;
                    });
                  }
                  
                  // Sort groups by Grade (09, 10, 11, 12) then Term (1, 2, 3) then Institution
                  const sortedEntries = Object.entries(groupedCourses).sort((a, b) => {
                    const [gradeA, termA, instA] = a[0].split('|');
                    const [gradeB, termB, instB] = b[0].split('|');
                    
                    // Extract numeric grade (e.g., "Grade 10" -> 10)
                    const gradeNumA = parseInt(gradeA.replace(/\D/g, '')) || 0;
                    const gradeNumB = parseInt(gradeB.replace(/\D/g, '')) || 0;
                    
                    if (gradeNumA !== gradeNumB) return gradeNumA - gradeNumB;
                    
                    // Extract numeric term (e.g., "Term 1" -> 1)
                    const termNumA = parseInt(termA.replace(/\D/g, '')) || 0;
                    const termNumB = parseInt(termB.replace(/\D/g, '')) || 0;
                    
                    if (termNumA !== termNumB) return termNumA - termNumB;
                    
                    // Sort by institution (DVHS first, then alphabetically)
                    if (instA.includes('Dougherty Valley') && !instB.includes('Dougherty Valley')) return -1;
                    if (!instA.includes('Dougherty Valley') && instB.includes('Dougherty Valley')) return 1;
                    return instA.localeCompare(instB);
                  });
                  
                  return sortedEntries.map(([key, courses]) => {
                    const [grade, term, institution] = key.split('|');
                    const isDVHS = institution.includes('Dougherty Valley');
                    // For future terms and regular terms, use consistent format with institution
                    const displayHeader = courses.isFutureTerm 
                      ? (isDVHS ? `Grade ${grade} - Term ${term}` : `Grade ${grade} - Term ${term} (${institution})`)
                      : (isDVHS ? `${grade} - ${term}` : `${grade} - ${term} (${institution})`);
                    const isCollapsed = collapsedTerms.has(key);
                    
                    return [
                      <tr key={key + '-header'} className="sticky" style={{ position: 'sticky', top: '48px', zIndex: 5 }}>
                        <td colSpan={3} className={`font-bold text-base py-4 px-6 border-b-2 ${
                          courses.isFutureTerm 
                            ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/20 border-l-4 border-green-500 border-b-green-300 dark:border-b-green-700' 
                            : 'bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-800/80 border-b-gray-300 dark:border-b-gray-600'
                        }`}>
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => toggleTermCollapse(key)}
                              className="flex items-center space-x-3 text-left hover:text-indigo-600 dark:hover:text-indigo-400 transition-all group"
                            >
                              <ChevronDownIcon 
                                className={`h-5 w-5 transition-transform duration-300 ${isCollapsed ? '-rotate-90' : 'rotate-0'} group-hover:scale-110`}
                              />
                              <span className={`${courses.isFutureTerm ? 'text-green-800 dark:text-green-200' : 'text-gray-900 dark:text-gray-100'} tracking-tight`}>
                                {displayHeader}
                              </span>
                              {courses.isFutureTerm && (
                                <span className="px-2.5 py-1 bg-green-200 dark:bg-green-700 text-green-800 dark:text-green-100 text-xs rounded-full font-bold shadow-sm">
                                  What If? Term
                                </span>
                              )}
                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded-full">
                                {courses.length} {courses.length !== 1 ? 'courses' : 'course'}
                              </span>
                            </button>
                            {courses.isFutureTerm && whatIfMode && (
                              <div className="flex items-center space-x-2">
                                <FutureTermAddCourse 
                                  termId={courses.futureTermId}
                                  onAddCourse={addFutureCourse}
                                  allCourses={allCourses}
                                />
                                <button
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to remove this what-if term and all its courses?')) {
                                      removeFutureTerm(courses.futureTermId);
                                    }
                                  }}
                                  className="px-3 py-[0.438rem] bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-all shadow-md hover:shadow-lg hover:scale-105 flex items-center space-x-1"
                                  title="Remove What If? Term"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  <span>Remove Term</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>,
                      ...(isCollapsed ? [] : (() => {
                        // Build course list with preview
                        const courseList = [];
                        const isDraggingInThisTerm = dragPreviewPosition && dragPreviewPosition.termKey === key;
                        const draggedFromThisTerm = draggedCourse && draggedCourse.sourceTermKey === key;
                        
                        courses.forEach((course, index) => {
                      // Find the index in the original courses array, not the flattened array
                      const courseIndex = transcript.courses.findIndex(c => 
                        c.Course === course.Course && c.Grade === course.Grade && c.Term === course.Term
                      );
                      const originalCourse = transcript.courses.find(c => 
                        c.Course === course.Course && c.Grade === course.Grade && c.Term === course.Term
                      );
                      const isModified = whatIfMode && originalCourse && course.Mark !== originalCourse.Mark;
                      
                      // Check if this is the course being dragged
                      const isBeingDragged = draggedCourse && 
                        draggedCourse.course.Course === course.Course && 
                        draggedCourse.course.Grade === course.Grade &&
                        draggedCourse.course.Term === course.Term;
                      
                      // Check if this row is the drop target
                      const isDropTarget = dragPreviewPosition && 
                        dragPreviewPosition.index === index && 
                        dragPreviewPosition.termKey === key;
                      
                        const courseRow = (
                          <tr 
                            key={key + '-' + index} 
                            className={`transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                              isDropTarget
                                ? 'bg-indigo-100/80 dark:bg-indigo-900/40 border-t-4 border-t-indigo-600 dark:border-t-indigo-400 shadow-lg'
                                : isBeingDragged
                                ? 'opacity-40'
                                : course.isFuture 
                                ? 'bg-green-50/50 dark:bg-green-900/10 border-l-4 border-green-500 hover:bg-green-100/50 dark:hover:bg-green-900/20 cursor-move' 
                                : isModified 
                                ? 'bg-yellow-50/50 dark:bg-yellow-900/10 hover:bg-yellow-100/50 dark:hover:bg-yellow-900/20' 
                                : whatIfMode && courses.isFutureTerm
                                ? `${index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/50 dark:bg-gray-800/30'}`
                                : index % 2 === 0 
                                ? 'bg-white dark:bg-gray-900' 
                                : 'bg-gray-50/50 dark:bg-gray-800/30'
                            }`}
                            {...(whatIfMode && courses.isFutureTerm ? {
                              draggable: true,
                              onDragStart: (e) => handleCourseDragStart(e, course, key, true),
                              onDragOver: (e) => handleCourseDragOver(e, index, key),
                              onDragLeave: handleCourseDragLeave,
                              onDrop: (e) => handleCourseDrop(e, index, key),
                              onDragEnd: handleCourseDragEnd
                            } : {})}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700">
                              <div className="flex items-center space-x-2 flex-wrap">
                                <span>{course.Course || 'N/A'}</span>
                                {course.isFuture && !course.wasMoved && (
                                  <span className="px-2 py-0.5 bg-green-200 dark:bg-green-700 text-green-800 dark:text-green-100 text-xs rounded-full font-bold shadow-sm">
                                    What-If
                                  </span>
                                )}
                                {course.wasMoved && whatIfMode && (
                                  <span className="px-2 py-0.5 bg-green-200 dark:bg-green-700 text-green-800 dark:text-green-100 text-xs rounded-full font-medium shadow-sm">
                                    Was in {course.originalGrade} {course.originalTerm}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                              {course.Credits || 5.0}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  {whatIfMode ? (
                                  <select
                                    value={course.Mark}
                                    onChange={(e) => course.isFuture 
                                      ? handleFutureCourseGradeChange(course.id, e.target.value)
                                      : handleGradeChange(courseIndex, e.target.value)
                                    }
                                    className={`px-3 py-1.5 rounded-full text-xs font-bold border shadow-sm transition-all cursor-pointer hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                                      course.Mark === 'A' || course.Mark === 'A+' || course.Mark === 'A-'
                                        ? 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-600 focus:ring-green-500/20'
                                        : course.Mark === 'B' || course.Mark === 'B+' || course.Mark === 'B-'
                                        ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-600 focus:ring-blue-500/20'
                                        : course.Mark === 'C' || course.Mark === 'C+' || course.Mark === 'C-'
                                        ? 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-600 focus:ring-yellow-500/20'
                                        : course.Mark === 'D' || course.Mark === 'D+' || course.Mark === 'D-'
                                        ? 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-600 focus:ring-orange-500/20'
                                        : course.Mark === 'F'
                                        ? 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-600 focus:ring-red-500/20'
                                        : 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 focus:ring-gray-500/20'
                                    }`}
                                  >
                                    <option value="A+" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>A+</option>
                                    <option value="A" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>A</option>
                                    <option value="A-" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>A-</option>
                                    <option value="B+" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>B+</option>
                                    <option value="B" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>B</option>
                                    <option value="B-" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>B-</option>
                                    <option value="C+" style={{ backgroundColor: '#fef3c7', color: '#a16207' }}>C+</option>
                                    <option value="C" style={{ backgroundColor: '#fef3c7', color: '#a16207' }}>C</option>
                                    <option value="C-" style={{ backgroundColor: '#fef3c7', color: '#a16207' }}>C-</option>
                                    <option value="D+" style={{ backgroundColor: '#fed7aa', color: '#9a3412' }}>D+</option>
                                    <option value="D" style={{ backgroundColor: '#fed7aa', color: '#9a3412' }}>D</option>
                                    <option value="D-" style={{ backgroundColor: '#fed7aa', color: '#9a3412' }}>D-</option>
                                    <option value="F" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>F</option>
                                    <option value="P" style={{ backgroundColor: '#f3f4f6', color: '#374151' }}>P</option>
                                  </select>
                                ) : (
                                  <span className={`px-3 py-1.5 rounded-full text-xs font-bold shadow-sm ${
                                    course.Mark === 'A' || course.Mark === 'A+' || course.Mark === 'A-'
                                      ? 'bg-green-100 text-green-800 border border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-600'
                                      : course.Mark === 'B' || course.Mark === 'B+' || course.Mark === 'B-'
                                      ? 'bg-blue-100 text-blue-800 border border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-600'
                                      : course.Mark === 'C' || course.Mark === 'C+' || course.Mark === 'C-'
                                      ? 'bg-yellow-100 text-yellow-800 border border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-600'
                                      : course.Mark === 'D' || course.Mark === 'D+' || course.Mark === 'D-'
                                      ? 'bg-orange-100 text-orange-800 border border-orange-300 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-600'
                                      : course.Mark === 'F'
                                      ? 'bg-red-100 text-red-800 border border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-600'
                                      : 'bg-gray-100 text-gray-800 border border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
                                  }`}>
                                    {course.Mark}
                                    </span>
                                  )}
                                  {isModified && (() => {
                                    const newGrade = course.Mark || '';
                                    let wasColorClass = '';
                                    if (newGrade.startsWith('A')) {
                                      wasColorClass = 'text-green-700 bg-green-100 border-green-300 dark:text-green-300 dark:bg-green-900/40 dark:border-green-600';
                                    } else if (newGrade.startsWith('B')) {
                                      wasColorClass = 'text-blue-700 bg-blue-100 border-blue-300 dark:text-blue-300 dark:bg-blue-900/40 dark:border-blue-600';
                                    } else if (newGrade.startsWith('C')) {
                                      wasColorClass = 'text-yellow-700 bg-yellow-100 border-yellow-300 dark:text-yellow-300 dark:bg-yellow-900/40 dark:border-yellow-600';
                                    } else if (newGrade.startsWith('D')) {
                                      wasColorClass = 'text-orange-700 bg-orange-100 border-orange-300 dark:text-orange-300 dark:bg-orange-900/40 dark:border-orange-600';
                                    } else if (newGrade === 'F') {
                                      wasColorClass = 'text-red-700 bg-red-100 border-red-300 dark:text-red-300 dark:bg-red-900/40 dark:border-red-600';
                                    } else {
                                      wasColorClass = 'text-gray-700 bg-gray-100 border-gray-300 dark:text-gray-300 dark:bg-gray-700/40 dark:border-gray-600';
                                    }
                                    return (
                                      <span className={`text-xs font-medium border px-2 py-0.5 rounded-full whitespace-nowrap ${wasColorClass}`}>
                                        was {originalCourse.Mark}
                                      </span>
                                    );
                                  })()}
                                </div>
                                {course.isFuture && !course.wasMoved && whatIfMode && (
                                  <button
                                    onClick={() => removeFutureCourse(course.id)}
                                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full p-1 transition-all flex-shrink-0"
                                    title="Remove course"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                        
                        courseList.push(courseRow);
                      });
                      
                      return courseList;
                    })()),
                    ];
                  }).flat();
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Raw Text Fallback (if no structured data) */}
      {transcript.raw_text && (!transcript.courses || transcript.courses.length === 0) && (
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Extracted Transcript Text</h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <pre className="whitespace-pre-wrap text-sm text-gray-700">
              {transcript.raw_text}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper to robustly display GPA values
function displayGPA(val) {
  if (val === null || val === undefined || val === '' || val === 'null') return 'Not Available';
  return val;
}

// A-G Requirements Modal Component
function AGRequirementsModal({ category, onClose }) {
  // Add escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  
  const agRequirements = {
    'A': {
      title: 'History / Social Science',
      description: 'Two years of history/social science required, including: one year of world history, cultures or historical geography and one year of U.S. history; or one-half year of U.S. history and one-half year of civics or American government.',
      studentFriendly: 'You need 2 years total: 1 year of world history AND 1 year of U.S. history (or half-year U.S. history + half-year civics/government).'
    },
    'B': {
      title: 'English',
      description: 'Four years of college preparatory English composition and literature required, integrating extensive reading, frequent writing, and practice listening and speaking with different audiences. Students may only use 1 year of ESL/ELD English.',
      studentFriendly: 'You need 4 years of English with lots of reading, writing, and speaking practice. Only 1 year can be ESL/ELD English.'
    },
    'C': {
      title: 'Mathematics',
      description: 'Three years of college-preparatory mathematics (four years are strongly recommended), including or integrating topics covered in elementary algebra, advanced algebra, and two-and three-dimensional geometry.',
      studentFriendly: 'You need at least 3 years of math (4 years recommended) covering algebra, advanced algebra, and geometry.'
    },
    'D': {
      title: 'Science',
      description: 'Two years of science are required (three years are strongly recommended), providing fundamental knowledge in two of the following: biology, chemistry, or physics. A yearlong interdisciplinary or earth and space science course can meet one year of this requirement. Science courses listed under engineering, computer science and applied science disciplines can only be used to fulfill the recommended third year.',
      studentFriendly: 'You need at least 2 years of science (3 years recommended) from biology, chemistry, or physics. Earth/space science can count for 1 year.'
    },
    'E': {
      title: 'Language Other than English',
      description: 'Two years (or through the second level of high school instruction) of the same language other than English (three years recommended). LOTE levels are defined by the number of years of high school instruction; e.g. LOTE 1= 1 yr.; LOTE 2 = 2 years, etc.',
      studentFriendly: 'You need 2 years of the same foreign language (3 years recommended). Must be the same language - you can\'t mix Spanish 1 with French 1.'
    },
    'F': {
      title: 'Visual & Performing Arts',
      description: 'One year required, chosen from one of the following categories: dance, music, theater, interdisciplinary arts, or visual arts (e.g., painting, web/graphic design, film/video, inter/multimedia arts). Two one-semester courses from the same discipline is also acceptable.',
      studentFriendly: 'You need 1 year of arts: dance, music, theater, or visual arts. Two semester courses from the same area also work.'
    },
    'G': {
      title: 'College-Preparatory Elective',
      description: 'One unit (equivalent to one year) chosen from the "a-f" courses beyond those used to satisfy the requirements of the "a-f" subjects, or courses that have been approved solely in the elective area.',
      studentFriendly: 'You need 1 additional year from any A-F subject area beyond the minimum requirements, or from approved elective courses.'
    }
  };

  const requirement = agRequirements[category];
  if (!requirement) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-all duration-200">
      <div className="relative bg-white/95 backdrop-blur-md dark:bg-gray-900/95 rounded-2xl shadow-xl max-w-2xl w-full mx-4 p-6 border border-gray-200/50 dark:border-gray-700/50">
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl font-bold focus:outline-none"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
        
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium">
              A-G {category}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{requirement.title}</h2>
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
            <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">In Simple Terms:</h3>
            <p className="text-blue-700 dark:text-blue-300 text-sm leading-relaxed">{requirement.studentFriendly}</p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Official UC Requirement:</h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">{requirement.description}</p>
          </div>
          
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            <p>* Based on University of California (UC) admission requirements</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Existing components (CourseCard, GPTAdvisor, etc.) remain the same...
function CourseCard({ course, onSelect, selected, selectMode, modalView, onAGTagClick }) {
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [hasSubmittedReview, setHasSubmittedReview] = useState(false);
  const [reviewData, setReviewData] = useState({
    difficulty: 3,
    workload: 3,
    overall: 4
  });
  const [courseStats, setCourseStats] = useState({ overall: 0, difficulty: 0, workload: 0, count: 0 });
  const [loadingStats, setLoadingStats] = useState(true);

  // Fetch course review stats from backend
  useEffect(() => {
    const fetchCourseStats = async () => {
      try {
        const response = await api.get(`/courses/${course.code}/stats`);
        setCourseStats(response.data);
        setLoadingStats(false);
      } catch (error) {
        console.error('Error fetching course stats:', error);
        setLoadingStats(false);
      }
    };
    if (course.code) {
      fetchCourseStats();
    }
  }, [course.code]);

  const getDifficultyColor = (level) => {
    // 1-2: Easy (Green)
    if (level <= 2) return 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-600';
    // 3: Medium (Yellow)
    if (level === 3) return 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-600';
    // 4-5: Hard (Red)
    return 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-600';
  };

  // Check if user has already submitted a review for this course
  useEffect(() => {
    const checkExistingReview = async () => {
      try {
        const response = await api.get(`/courses/${course.code}/my-review`);
        if (response.data && response.data.review) {
          setHasSubmittedReview(true);
          setReviewData({
            overall: response.data.review.ratings.overall,
            difficulty: response.data.review.ratings.difficulty,
            workload: response.data.review.ratings.workload
          });
        }
      } catch (error) {
        // No review found or error - user hasn't submitted
        setHasSubmittedReview(false);
      }
    };
    checkExistingReview();
  }, [course.code]);

  const getTypeColor = (type) => {
    if (type === 'AP') return 'bg-purple-100 text-purple-700 border-purple-200';
    if (type === 'Honors') return 'bg-blue-100 text-blue-700 border-blue-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // Extract A-G tag from course description or tags
  const getAGTag = (course) => {
    // Only show A-G tag if there's a specific letter (A-G) in the ag_tag field
    if (course.ag_tag && course.ag_tag.trim()) {
      const agLetter = course.ag_tag.trim().toUpperCase();
      // Check if it's a valid A-G letter
      if (/^[A-G]$/.test(agLetter)) {
        return { tag: `A-G ${agLetter}`, category: agLetter };
      }
    }
    
    // No A-G tag - return null to indicate no badge should be shown
    return null;
  };

  const handleAGTagClick = (e, category) => {
    e.stopPropagation(); // Prevent card click
    if (category && onAGTagClick) {
      onAGTagClick(category);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (hasSubmittedReview) {
        // Update existing review
        await api.put(`/courses/${course.code}/my-review`, {
          ratings: {
            overall: reviewData.overall,
            difficulty: reviewData.difficulty,
            workload: reviewData.workload
          },
          comment: null
        });
        // No alert for updates - silent success
      } else {
        // Submit new review
        await api.post(`/courses/${course.code}/reviews`, {
          course_code: course.code,
          ratings: {
            overall: reviewData.overall,
            difficulty: reviewData.difficulty,
            workload: reviewData.workload
          },
          comment: null
        });
        alert('Review submitted successfully!');
      }

      // Refresh stats immediately after submission
      const statsResponse = await api.get(`/courses/${course.code}/stats`);
      setCourseStats(statsResponse.data);

      setShowReviewForm(false);
      setHasSubmittedReview(true);
    } catch (error) {
      console.error('Error submitting review:', error);
      const errorMsg = error.response?.data?.detail;
      const displayMsg = typeof errorMsg === 'string' ? errorMsg : 'Failed to submit review. Please try again.';
      alert(displayMsg);
    }
  };

  return (
    <div 
      className={
        modalView
          ? 'bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 transition-all duration-200'
          : `rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-5 cursor-pointer border-2 ${
              selectMode 
                ? (selected 
                    ? 'bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border-purple-500 ring-4 ring-purple-300 dark:ring-purple-700 shadow-lg shadow-purple-500/50' 
                    : 'bg-white dark:bg-gray-900 border-purple-300 dark:border-purple-700 shadow-md shadow-purple-500/20 hover:shadow-lg hover:shadow-purple-500/40')
                : (selected 
                    ? 'bg-white dark:bg-gray-900 border-indigo-500 ring-2 ring-indigo-200 dark:ring-indigo-800' 
                    : 'bg-white dark:bg-gray-900 border-transparent hover:border-gray-200 dark:hover:border-gray-700')
            } ${selectMode ? '' : 'hover:shadow-xl'} relative`
      }
      onClick={() => !modalView && onSelect(course)}
      style={modalView ? { cursor: 'default' } : {}}
    >
      {/* Selected Checkmark Badge */}
      {selectMode && selected && (
        <div className="absolute -top-2 -right-2 [background:linear-gradient(to_bottom_right,var(--accent-500),var(--accent-600))] text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg ring-2 ring-white dark:ring-gray-900 z-10">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 mr-3">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 leading-tight">{course.name}</h3>
          {(() => {
            const agInfo = getAGTag(course);
            return agInfo ? (
              <button 
                className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 text-sm font-medium underline cursor-pointer transition-colors"
                onClick={(e) => handleAGTagClick(e, agInfo.category)}
              >
                {agInfo.tag}
              </button>
            ) : (
              <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">{course.code}</p>
            );
          })()}
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${getTypeColor(course.type)}`}>
          {course.type}
        </span>
      </div>

      <p className={`text-gray-700 dark:text-gray-300 mb-3 text-xs leading-relaxed ${modalView ? '' : 'line-clamp-2'}`}>{course.description}</p>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="flex items-center space-x-1.5">
          <div className={`px-2 py-1 rounded-lg text-xs font-semibold border ${
            loadingStats ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' : 
            courseStats.count > 0 ? getDifficultyColor(courseStats.difficulty) : 
            'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
          }`}>
            {loadingStats ? 'Loading...' : courseStats.count > 0 ? `Difficulty: ${courseStats.difficulty.toFixed(1)}/5` : 'No reviews yet'}
          </div>
        </div>
        
        <div className="flex items-center space-x-1">
          <ClockIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
            {loadingStats ? '...' : courseStats.count > 0 ? `${courseStats.workload.toFixed(1)}/5` : 'N/A'}
          </span>
        </div>
        
        <div className="flex items-center space-x-1">
          <StarIcon className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
          <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
            {loadingStats ? '...' : courseStats.count > 0 ? `${courseStats.overall.toFixed(1)}/5` : 'N/A'}
          </span>
        </div>
        
        <div className="flex items-center space-x-1">
          <svg className="h-4 w-4 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
            {loadingStats ? '...' : `${courseStats.count} ${courseStats.count === 1 ? 'review' : 'reviews'}`}
          </span>
        </div>
      </div>

      <div className="text-xs text-gray-600 dark:text-gray-300 font-medium pt-2 border-t border-gray-100 dark:border-gray-800">
        <span className="text-gray-500 dark:text-gray-400">Department:</span> {course.department}
      </div>
      
      {course.prerequisites.length > 0 && (
        <div className="text-xs text-gray-500 mt-1">
          <span className="font-medium">Prerequisites:</span> {course.prerequisites.join(', ')}
        </div>
      )}

      {/* Review Section - Only show in modal view */}
      {modalView && (
        <div className="mt-6 pt-6 border-t-2 border-gray-200 dark:border-gray-700">
          {!showReviewForm ? (
            <button
              onClick={() => setShowReviewForm(true)}
              className="w-full px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
            >
              {hasSubmittedReview ? '✏️ Edit Your Review' : '⭐ Write a Review'}
            </button>
          ) : (
            <form onSubmit={handleReviewSubmit} className="space-y-4">
              <h4 className="text-base font-bold text-gray-900 dark:text-white mb-3">
                {hasSubmittedReview ? 'Edit your review' : 'Rate this course'}
              </h4>
              
              {/* Difficulty Rating */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Difficulty
                  </label>
                  <span className={`text-sm font-bold px-2 py-0.5 rounded border ${getDifficultyColor(reviewData.difficulty)}`}>{reviewData.difficulty}/5</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={reviewData.difficulty}
                  onChange={(e) => setReviewData({...reviewData, difficulty: parseInt(e.target.value)})}
                  className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
              </div>

              {/* Workload */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Workload
                  </label>
                  <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{reviewData.workload}/5</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={reviewData.workload}
                  onChange={(e) => setReviewData({...reviewData, workload: parseInt(e.target.value)})}
                  className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
              </div>

              {/* Overall Rating */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Overall
                  </label>
                  <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{reviewData.overall}/5 ⭐</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={reviewData.overall}
                  onChange={(e) => setReviewData({...reviewData, overall: parseInt(e.target.value)})}
                  className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex space-x-2 pt-2">
                <button
                  type="submit"
                  className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-all"
                >
                  {hasSubmittedReview ? '✓ Update' : '✓ Submit'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowReviewForm(false)}
                  className="flex-1 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold transition-all"
                >
                  ✕ Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function ScheduleBuilder({ selectedCourses, onClose }) {
  const [scheduleView, setScheduleView] = useState('grid'); // 'grid' or 'list'
  const [showAGModal, setShowAGModal] = useState(false);
  const [selectedAGCategory, setSelectedAGCategory] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [courses, setCourses] = useState(selectedCourses);
  
  // Sync courses with selectedCourses prop
  useEffect(() => {
    setCourses(selectedCourses);
  }, [selectedCourses]);
  
  // Add escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  
  const getTypeColor = (type) => {
    if (type === 'AP') return 'bg-purple-100 text-purple-700 border-purple-200';
    if (type === 'Honors') return 'bg-blue-100 text-blue-700 border-blue-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getDifficultyColor = (level) => {
    // 1-2: Easy (Green)
    if (level <= 2) return 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-600';
    // 3: Medium (Yellow)
    if (level === 3) return 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-600';
    // 4-5: Hard (Red)
    return 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-600';
  };

  // Extract A-G tag from course description or tags
  const getAGTag = (course) => {
    // Only show A-G tag if there's a specific letter (A-G) in the ag_tag field
    if (course.ag_tag && course.ag_tag.trim()) {
      const agLetter = course.ag_tag.trim().toUpperCase();
      // Check if it's a valid A-G letter
      if (/^[A-G]$/.test(agLetter)) {
        return { tag: `A-G ${agLetter}`, category: agLetter };
      }
    }
    
    // No A-G tag - return null to indicate no badge should be shown
    return null;
  };

  const handleAGTagClick = (e, category) => {
    e.stopPropagation();
    if (category) {
      setSelectedAGCategory(category);
      setShowAGModal(true);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newCourses = [...courses];
    const draggedCourse = newCourses[draggedIndex];
    
    // Remove dragged course
    newCourses.splice(draggedIndex, 1);
    
    // Insert at new position
    newCourses.splice(dropIndex, 0, draggedCourse);
    
    setCourses(newCourses);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-all duration-200" onClick={onClose}>
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-6xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto border-2 border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl font-bold focus:outline-none z-10"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
        
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Your Course Schedule</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">Viewing {courses.length} selected course{courses.length !== 1 ? 's' : ''}</p>
        </div>

        {courses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-lg">No courses selected</p>
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium"
            >
              Go Back
            </button>
          </div>
        ) : (
          <>
            {/* View Toggle */}
            <div className="flex justify-end mb-4">
              <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <button
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    scheduleView === 'grid' 
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                  onClick={() => setScheduleView('grid')}
                >
                  Grid View
                </button>
                <button
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    scheduleView === 'list' 
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                  onClick={() => setScheduleView('list')}
                >
                  List View
                </button>
              </div>
            </div>

            {scheduleView === 'grid' ? (
              /* Grid View */
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {courses.map((course, index) => (
              <div key={course.id} className="bg-white dark:bg-gray-900 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-5 border-2 border-transparent hover:border-gray-200 dark:hover:border-gray-700">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 mr-3">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 leading-tight">{course.name}</h3>
                    {(() => {
                      const agInfo = getAGTag(course);
                      return agInfo ? (
                        <button 
                          className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 text-sm font-medium underline cursor-pointer transition-colors"
                          onClick={(e) => handleAGTagClick(e, agInfo.category)}
                        >
                          {agInfo.tag}
                        </button>
                      ) : (
                        <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">{course.code}</p>
                      );
                    })()}
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold border flex-shrink-0 ${getTypeColor(course.type)}`}>
                    {course.type}
                  </span>
                </div>
                
                <p className="text-gray-700 dark:text-gray-300 text-sm mb-4 line-clamp-2">{course.description}</p>
                
                {/* Stats section matching Course Planner */}
                <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                  {/* Difficulty and Workload */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {course.difficulty_level ? (
                        <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${getDifficultyColor(course.difficulty_level)}`}>
                          Difficulty: {course.difficulty_level}/5
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-md text-xs font-semibold border bg-gray-100 text-gray-500 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600">
                          Difficulty: N/A
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                      <ClockIcon className="h-4 w-4" />
                      <span className="font-medium">{course.avg_homework_hours ? `${course.avg_homework_hours} hrs` : 'N/A'}</span>
                    </div>
                  </div>
                  
                  {/* Rating and Department */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                      <StarIcon className="h-4 w-4 text-yellow-500" />
                      <span className="font-medium">{course.overall_rating ? `${course.overall_rating}/5` : 'No reviews yet'}</span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Department: {course.department}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="space-y-3">
            {courses.map((course, index) => (
              <div key={`course-${course.id}-${index}`}>
                {/* Drop zone indicator */}
                {dragOverIndex === index && draggedIndex !== index && (
                  <div className="h-2 bg-indigo-200 dark:bg-indigo-700 rounded-full mb-2 animate-pulse">
                    <div className="h-full bg-indigo-500 rounded-full w-full"></div>
                  </div>
                )}
                
                <div 
                  className={`bg-white dark:bg-gray-800 rounded-xl border-2 p-5 cursor-move transition-all duration-200 ${
                    draggedIndex === index 
                      ? 'opacity-30 scale-95 border-indigo-400 shadow-xl' 
                      : dragOverIndex === index 
                        ? 'border-indigo-500 shadow-lg transform translate-y-1' 
                        : 'border-gray-200 dark:border-gray-700 hover:shadow-lg hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 text-sm font-bold">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 dark:text-white text-base">{course.name}</h3>
                        <p className="text-gray-600 dark:text-gray-300 text-sm font-medium flex items-center space-x-2">
                          {(() => {
                            const agInfo = getAGTag(course);
                            return agInfo ? (
                              <button 
                                className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 underline cursor-pointer transition-colors"
                                onClick={(e) => handleAGTagClick(e, agInfo.category)}
                              >
                                {agInfo.tag}
                              </button>
                            ) : (
                              <span>{course.code}</span>
                            );
                          })()}
                          <span className="text-gray-400">•</span>
                          <span>{course.department}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm">
                    <span className={`px-3 py-1.5 rounded-full text-xs font-bold border ${getTypeColor(course.type)}`}>
                      {course.type}
                    </span>
                    {course.difficulty_level ? (
                      <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${getDifficultyColor(course.difficulty_level)}`}>
                        Difficulty: {course.difficulty_level}/5
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 rounded-md text-xs font-semibold border bg-gray-100 text-gray-500 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600">
                        Difficulty: N/A
                      </span>
                    )}
                    <span className="text-gray-600 dark:text-gray-400 font-medium flex items-center space-x-1">
                      <ClockIcon className="h-4 w-4" />
                      <span>{course.avg_homework_hours ? `${course.avg_homework_hours} hrs` : 'N/A'}</span>
                    </span>
                    <span className="text-gray-600 dark:text-gray-400 font-medium flex items-center space-x-1">
                      <StarIcon className="h-4 w-4 text-yellow-500" />
                      <span>{course.overall_rating ? `${course.overall_rating}/5` : 'N/A'}</span>
                    </span>
                  </div>
                </div>
                
                <p className="text-gray-700 dark:text-gray-200 text-sm mt-3 pl-12 line-clamp-2">{course.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}

            {/* Summary Stats */}
            <div className="mt-6 pt-6 border-t-2 border-gray-200 dark:border-gray-700">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-4">Schedule Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                {/* Total Courses */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 text-center border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white mb-1">{courses.length}</div>
                  <div className="text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide leading-tight">Total Courses</div>
                  <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-[var(--accent-500)]" style={{width: `${Math.min((courses.length / 7) * 100, 100)}%`}}></div>
                  </div>
                </div>
                
                {/* Advanced Courses */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 text-center border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white mb-1">
                    {courses.filter(c => c.type === 'AP' || c.type === 'Honors').length}
                  </div>
                  <div className="text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide leading-tight">Advanced Courses</div>
                  <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-[var(--accent-500)]" style={{width: `${courses.length > 0 ? (courses.filter(c => c.type === 'AP' || c.type === 'Honors').length / courses.length) * 100 : 0}%`}}></div>
                  </div>
                </div>
                
                {/* Avg Difficulty (out of 5) */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 text-center border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white mb-1">
                    {(() => {
                      const coursesWithDifficulty = courses.filter(c => c.difficulty_level);
                      const totalDifficulty = coursesWithDifficulty.reduce((sum, c) => sum + c.difficulty_level, 0);
                      const avgDifficulty = coursesWithDifficulty.length > 0 ? (totalDifficulty / coursesWithDifficulty.length).toFixed(1) : 'N/A';
                      return avgDifficulty === 'N/A' ? avgDifficulty : `${avgDifficulty}/5`;
                    })()}
                  </div>
                  <div className="text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide leading-tight">Avg Difficulty</div>
                  <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-[var(--accent-500)]" style={{width: `${(() => {
                      const coursesWithDifficulty = courses.filter(c => c.difficulty_level);
                      const totalDifficulty = coursesWithDifficulty.reduce((sum, c) => sum + c.difficulty_level, 0);
                      const avgDifficulty = courses.length > 0 ? (totalDifficulty / courses.length) : 0;
                      return (avgDifficulty / 5) * 100;
                    })()}%`}}></div>
                  </div>
                </div>
                
                {/* Avg Workload (hours per week) */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 text-center border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white mb-1">
                    {(() => {
                      const coursesWithWorkload = courses.filter(c => c.avg_homework_hours);
                      const totalWorkload = coursesWithWorkload.reduce((sum, c) => sum + c.avg_homework_hours, 0);
                      const avgWorkload = coursesWithWorkload.length > 0 ? (totalWorkload / coursesWithWorkload.length).toFixed(1) : 'N/A';
                      return avgWorkload === 'N/A' ? avgWorkload : `${avgWorkload}h`;
                    })()}
                  </div>
                  <div className="text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide leading-tight">Avg HW/Week</div>
                  <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-[var(--accent-500)]" style={{width: `${(() => {
                      const coursesWithWorkload = courses.filter(c => c.avg_homework_hours);
                      const totalWorkload = coursesWithWorkload.reduce((sum, c) => sum + c.avg_homework_hours, 0);
                      const avgWorkload = coursesWithWorkload.length > 0 ? (totalWorkload / coursesWithWorkload.length) : 0;
                      return Math.min((avgWorkload / 10) * 100, 100);
                    })()}%`}}></div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* A-G Requirements Modal */}
      {showAGModal && selectedAGCategory && (
        <AGRequirementsModal 
          category={selectedAGCategory} 
          onClose={() => {
            setShowAGModal(false);
            setSelectedAGCategory(null);
          }} 
        />
      )}
    </div>
  );
}

function CoursePlanner() {
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState(localStorage.getItem('cp_search') || '');
  const [typeFilter, setTypeFilter] = useState(localStorage.getItem('cp_type') || '');
  const [departmentFilter, setDepartmentFilter] = useState(localStorage.getItem('cp_dept') || '');
  const [agFilter, setAgFilter] = useState(localStorage.getItem('cp_ag') || '');
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [modalCourse, setModalCourse] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(12); // Show 12 courses per page
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showAGModal, setShowAGModal] = useState(false);
  const [selectedAGCategory, setSelectedAGCategory] = useState(null);
  const maxCourses = 7;
  
  // Hide-on-scroll for mobile
  const isHeaderVisible = useHideOnScroll();

  useEffect(() => {
    fetchCourses();
    fetchDepartments();
  }, []);

  useEffect(() => {
    const timer = filterCourses();
    return timer;
  }, [courses, searchTerm, typeFilter, departmentFilter, agFilter, currentPage, coursesPerPage]);

  // Persist filters to localStorage
  useEffect(() => {
    localStorage.setItem('cp_search', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem('cp_type', typeFilter);
  }, [typeFilter]);

  useEffect(() => {
    localStorage.setItem('cp_dept', departmentFilter);
  }, [departmentFilter]);

  useEffect(() => {
    localStorage.setItem('cp_ag', agFilter);
  }, [agFilter]);

  // Reset to first page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [searchTerm, typeFilter, departmentFilter, agFilter]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && modalCourse) {
        setModalCourse(null);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [modalCourse]);

  const fetchCourses = async () => {
    try {
      const response = await api.get(`/courses`);
      setCourses(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError('Failed to load courses. Please try again later.');
      setCourses([]);
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get(`/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // Helper function to extract A-G category from course
  const getAGCategory = (course) => {
    // Only return A-G category if there's a specific letter (A-G) in the ag_tag field
    if (course.ag_tag && course.ag_tag.trim()) {
      const agLetter = course.ag_tag.trim().toUpperCase();
      // Check if it's a valid A-G letter
      if (/^[A-G]$/.test(agLetter)) {
        return agLetter;
      }
    }
    
    // No valid A-G category
    return null;
  };

  const filterCourses = () => {
    const timer = setTimeout(() => {
      let filtered = courses;

      if (searchTerm) {
        filtered = filtered.filter(course =>
          course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          course.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          course.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      if (typeFilter) {
        filtered = filtered.filter(course => course.type === typeFilter);
      }

      if (departmentFilter) {
        filtered = filtered.filter(course => course.department === departmentFilter);
      }

      if (agFilter) {
        filtered = filtered.filter(course => {
          const category = getAGCategory(course);
          return category === agFilter;
        });
      }

      // Calculate pagination
      const calculatedTotalPages = Math.ceil(filtered.length / coursesPerPage);
      const startIndex = (currentPage - 1) * coursesPerPage;
      const endIndex = startIndex + coursesPerPage;
      const currentCourses = filtered.slice(startIndex, endIndex);

      setFilteredCourses(currentCourses);
      setTotalCount(filtered.length);
      setTotalPages(calculatedTotalPages);
    }, 300);

    return () => clearTimeout(timer);
  };

  const handleCardClick = (course) => {
    if (selectMode) {
      setSelectedCourses(prev => {
        const isSelected = prev.find(c => c.id === course.id);
        if (isSelected) {
          return prev.filter(c => c.id !== course.id);
        } else {
          if (prev.length < maxCourses) {
            return [...prev, course];
          } else {
            return prev; // Do not add more than maxCourses
          }
        }
      });
    } else {
      setModalCourse(course);
    }
  };

  const handleAGTagClick = (category) => {
    setSelectedAGCategory(category);
    setShowAGModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-900 rounded-xl p-6 animate-pulse">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="text-red-500 dark:text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Error Loading Courses</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              setLoading(true);
              fetchCourses();
            }}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Sticky Header/Filter Bar with Blur */}
      <div className={`sticky top-16 z-20 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 dark:bg-gray-950/95 dark:supports-[backdrop-filter]:bg-gray-950/70 border-b border-gray-200 dark:border-gray-800/70 transition-transform duration-300 ${isHeaderVisible ? 'translate-y-0' : 'md:translate-y-0 -translate-y-full'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 tracking-tight mb-1">Course Planner</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Discover and plan your DVHS course schedule.</p>
            </div>
            <div className="flex items-center space-x-3">
              {selectedCourses.length > 0 && (
                <button
                  className="px-4 py-2 rounded-lg font-semibold transition-colors bg-green-600 hover:bg-green-700 text-white focus:outline-none focus:ring-2 focus:ring-green-400"
                  onClick={() => setShowSchedule(true)}
                >
                  View Schedule ({selectedCourses.length})
                </button>
              )}
              <div className="flex items-center gap-2">
                <button
                  className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-400 ${
                    selectMode 
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg ring-2 ring-purple-300 dark:ring-purple-700' 
                      : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:shadow-lg hover:scale-105'
                  }`}
                  onClick={() => setSelectMode(m => !m)}
                >
                  {selectMode ? '✓ Done Planning' : '📅 Plan Your Schedule'}
                </button>
                <HelpTooltip 
                  content="Select up to 7 courses to plan your schedule for the upcoming year. Click courses to add them to your schedule!"
                  position="left"
                />
              </div>
            </div>
          </div>

          {/* Selection Mode Indicator */}
          {selectMode && (
            <div className="mb-6 p-4 [background:linear-gradient(to_right,color-mix(in_srgb,var(--accent-50)_80%,white),color-mix(in_srgb,var(--accent-100)_60%,white))] dark:[background:linear-gradient(to_right,color-mix(in_srgb,var(--accent-900)_20%,transparent),color-mix(in_srgb,var(--accent-800)_15%,transparent))] [border-left:4px_solid_var(--accent-500)] rounded-lg shadow-sm">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  <span className="text-2xl">📅</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    💡 Click on course tiles to add them to your schedule! You can select up to {maxCourses} courses.
                  </p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-gray-700 dark:text-gray-300">
                      Selected: {selectedCourses.length} / {maxCourses}
                    </span>
                    <div className="flex-1 mx-4 [background:color-mix(in_srgb,var(--accent-200)_50%,transparent)] dark:[background:color-mix(in_srgb,var(--accent-800)_50%,transparent)] rounded-full h-2 overflow-hidden">
                      <div 
                        className="[background:linear-gradient(to_right,var(--accent-500),var(--accent-600))] h-full transition-all duration-300"
                        style={{ width: `${(selectedCourses.length / maxCourses) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search courses..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-800 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="relative">
                <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                <select
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-800 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-200 text-sm appearance-none cursor-pointer transition-all"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="AP">AP</option>
                  <option value="Honors">Honors</option>
                  <option value="Regular">Regular</option>
                </select>
                <ChevronDownIcon className="h-4 w-4 text-gray-400 absolute right-3 top-3.5 pointer-events-none" />
              </div>

              <div className="relative">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                <select
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-800 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-200 text-sm appearance-none cursor-pointer transition-all"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <option value="">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
                <ChevronDownIcon className="h-4 w-4 text-gray-400 absolute right-3 top-3.5 pointer-events-none" />
              </div>

              <div className="relative">
                <AcademicCapIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                <select
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-800 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-200 text-sm appearance-none cursor-pointer transition-all"
                  value={agFilter}
                  onChange={(e) => setAgFilter(e.target.value)}
                >
                  <option value="">All A-G Categories</option>
                  <option value="A">A-G A (History/Social Science)</option>
                  <option value="B">A-G B (English)</option>
                  <option value="C">A-G C (Mathematics)</option>
                  <option value="D">A-G D (Science)</option>
                  <option value="E">A-G E (Language Other than English)</option>
                  <option value="F">A-G F (Visual & Performing Arts)</option>
                  <option value="G">A-G G (College-Preparatory Elective)</option>
                </select>
                <ChevronDownIcon className="h-4 w-4 text-gray-400 absolute right-3 top-3.5 pointer-events-none" />
              </div>
            </div>
            
            {/* Clear Filters Button */}
            {(searchTerm || typeFilter || departmentFilter || agFilter) && (
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setTypeFilter('');
                    setDepartmentFilter('');
                    setAgFilter('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

      {/* Course Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCourses.map(course => (
          <CourseCard
            key={course.id}
            course={course}
            onSelect={handleCardClick}
            selected={selectMode && selectedCourses.find(c => c.id === course.id)}
            selectMode={selectMode}
            disabled={selectMode && !selectedCourses.find(c => c.id === course.id) && selectedCourses.length >= maxCourses}
            onAGTagClick={handleAGTagClick}
          />
        ))}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-8 space-x-2">
          <button
            onClick={() => {
              setCurrentPage(prev => Math.max(prev - 1, 1));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
          >
            Previous
          </button>
          
          {/* Page Numbers */}
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
            // Show first page, last page, current page, and pages around current
            const showPage = page === 1 || page === totalPages || 
                            (page >= currentPage - 2 && page <= currentPage + 2);
            
            if (!showPage) {
              // Show ellipsis for gaps
              if (page === currentPage - 3 || page === currentPage + 3) {
                return <span key={page} className="px-2 text-gray-400 dark:text-gray-500">...</span>;
              }
              return null;
            }
            
            return (
              <button
                key={page}
                onClick={() => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`px-4 py-2 rounded-xl transition-all font-medium ${
                  currentPage === page
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            );
          })}
          
          <button
            onClick={() => {
              setCurrentPage(prev => Math.min(prev + 1, totalPages));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
          >
            Next
          </button>
        </div>
      )}

      {/* Modal for course details */}
      {modalCourse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-all duration-200" onClick={() => setModalCourse(null)}>
          <div
            className="relative bg-white/95 backdrop-blur-md dark:bg-gray-900/95 rounded-2xl shadow-lg max-w-2xl w-full mx-4 p-6 border border-gray-200/50 dark:border-gray-700/50"
            onClick={e => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl font-bold focus:outline-none"
              onClick={() => setModalCourse(null)}
              aria-label="Close"
            >
              &times;
            </button>
            <CourseCard course={modalCourse} selectMode={false} onSelect={() => {}} selected={false} modalView={true} onAGTagClick={handleAGTagClick} />
          </div>
        </div>
      )}

      {filteredCourses.length === 0 && !loading && (
        <div className="text-center py-16">
          <div className="mb-6 relative">
            <BookOpenIcon className="h-24 w-24 text-gray-300 dark:text-gray-600 mx-auto" />
            <div className="absolute inset-0 flex items-center justify-center">
              <SparklesIcon className="h-12 w-12 text-indigo-400 opacity-50 animate-pulse" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No courses found</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Try adjusting your search or filter criteria</p>
          <button
            onClick={() => {
              setSearchTerm('');
              setTypeFilter('');
              setDepartmentFilter('');
              setAgFilter('');
            }}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* Schedule Builder */}
      {showSchedule && (
        <ScheduleBuilder selectedCourses={selectedCourses} onClose={() => setShowSchedule(false)} />
      )}

      {/* A-G Requirements Modal */}
      {showAGModal && selectedAGCategory && (
        <AGRequirementsModal 
          category={selectedAGCategory} 
          onClose={() => {
            setShowAGModal(false);
            setSelectedAGCategory(null);
          }} 
        />
      )}
      </div>
    </div>
  );
}

function Teachers() {
  const { user } = useAuth(); // Add useAuth hook at component level
  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('alpha_asc');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [teachersPerPage] = useState(12); // Show 12 teachers per page
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Hide-on-scroll for mobile
  const isHeaderVisible = useHideOnScroll();

  // Department color mapping (modern pill style)
  const departmentColors = {
    'Fine and Applied Arts (VAPA)': 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-500/30',
    'Mathematics': 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-500/30',
    'Physical Education': 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-300 dark:border-green-500/30',
    'Science': 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-500/30',
    'Social Science': 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-500/30',
    'World Language': 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-500/30',
    'English': 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-500/30',
    'English Language Arts': 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-500/30'
  };
  
  // Review system state
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    courseId: '',
    ratings: {
      overall: 0,
      homeworkLoad: 0,
      timeliness: 0,
      rigor: 0,
      supportiveness: 0,
      clarity: 0
    },
    written: '',
    createdBy: null,
    anonymous: true,
    status: 'pending',
    edited: false
  });
  const [wordCount, setWordCount] = useState(0);

  // Review pagination state
  const [currentReviewPage, setCurrentReviewPage] = useState(1);
  const reviewsPerPage = 5; // Show 5 reviews per page

  // Course dropdown state
  const [allCourses, setAllCourses] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  

  // Review categories and labels (aligned with MongoDB schema)
  const reviewCategories = {
    overall: 'Overall',
    homeworkLoad: 'Homework Load',
    timeliness: 'Timeliness of Grading',
    rigor: 'Class Rigor',
    supportiveness: 'Teacher Supportiveness',
    clarity: 'Clarity of Teaching'
  };

  // Helper text for rating categories (shows what high/low means)
  const getRatingHelperText = (category, rating) => {
    const helpers = {
      overall: { low: 'Poor', high: 'Excellent' },
      homeworkLoad: { low: 'Very Light', high: 'Very Heavy' },
      timeliness: { low: 'Very Slow', high: 'Very Fast' },
      rigor: { low: 'Very Easy', high: 'Very Hard' },
      supportiveness: { low: 'Not Supportive', high: 'Very Supportive' },
      clarity: { low: 'Unclear', high: 'Very Clear' }
    };
    
    if (rating === 0) return '';
    if (rating <= 2) return helpers[category].low;
    if (rating >= 4) return helpers[category].high;
    return 'Moderate';
  };

  // Helper Functions
  const RATING_LABEL = {
    5: "Amazing / Highly Recommend",
    4: "Great / Worthwhile",
    3: "Average / Mixed",
    2: "Below Average / Needs Improvement",
    1: "Poor / Wouldn't Recommend"
  };

  const ratingLine = (avg, count) => {
    if (!avg) return `No ratings yet • ${count} reviews`;
    const stars = "★★★★★".slice(0, Math.round(avg)).padEnd(5, "☆");
    return `${stars} • ${count} ${count === 1 ? "review" : "reviews"}`;
  };

  // Email generator function
  const generateEmail = (fullName) => {
    const parts = fullName.trim().toLowerCase().split(/\s+/);
    const first = parts[0] ?? "";
    const last = parts[parts.length - 1] ?? "";
    return `${first.charAt(0)}${last}@srvusd.net`;
  };

  // Real teacher data from CSV organized by departments
  const realTeachers = [
    // Fine and Applied Arts (VAPA)
    { _id: 1, name: 'Daniel Bowen', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 2, name: 'Andrew Cardiasmenos', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 3, name: 'Lauren Gibson', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 4, name: 'Albert Kim', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 5, name: 'Yin Lau', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 6, name: 'Ashley Lemay', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 7, name: 'Rajasree Mitra', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 8, name: 'Teri Musiel', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 9, name: 'David Offerman', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 10, name: 'Linda Pon', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 11, name: 'Paul Rhodes', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 12, name: 'Jeffrey Silverman', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 13, name: 'Kimberly Conroy-Tuohy', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 14, name: 'Diana Walker', department: 'Fine and Applied Arts (VAPA)' },
    { _id: 15, name: 'Kelsey Wengel', department: 'Fine and Applied Arts (VAPA)' },

    // Mathematics
    { _id: 16, name: 'Jil Assefa', department: 'Mathematics' },
    { _id: 17, name: 'Michelle Baker', department: 'Mathematics' },
    { _id: 18, name: 'Kevin Barth', department: 'Mathematics' },
    { _id: 19, name: 'Jennifer Cincotta', department: 'Mathematics' },
    { _id: 20, name: 'Robert Gendron', department: 'Mathematics' },
    { _id: 21, name: 'Kevin Guichard', department: 'Mathematics' },
    { _id: 22, name: 'Nirdeshika Guntakatta', department: 'Mathematics' },
    { _id: 23, name: 'Shannon Hancock', department: 'Mathematics' },
    { _id: 24, name: 'James Harari', department: 'Mathematics' },
    { _id: 25, name: 'Kellie Judson', department: 'Mathematics' },
    { _id: 26, name: 'Daniel Kravets', department: 'Mathematics' },
    { _id: 27, name: 'Philip Nho', department: 'Mathematics' },
    { _id: 28, name: 'Joyceanne Soliman', department: 'Mathematics' },
    { _id: 29, name: 'Evangeline Sugden', department: 'Mathematics' },
    { _id: 30, name: 'Chase Weise', department: 'Mathematics' },

    // Physical Education
    { _id: 31, name: 'Kasey Addiego', department: 'Physical Education' },
    { _id: 32, name: 'Ramona Altman', department: 'Physical Education' },
    { _id: 33, name: 'Brandon Croker', department: 'Physical Education' },
    { _id: 34, name: 'Monique Metzcus', department: 'Physical Education' },
    { _id: 35, name: 'Brian Nichols', department: 'Physical Education' },

    // Science
    { _id: 36, name: 'Mrinmoye Basu', department: 'Science' },
    { _id: 37, name: 'Kashiim Clemon', department: 'Science' },
    { _id: 38, name: 'Dean Covalt', department: 'Science' },
    { _id: 39, name: 'Karen Dennis', department: 'Science' },
    { _id: 40, name: 'Alissa Dreon', department: 'Science' },
    { _id: 41, name: 'Patrick Estes', department: 'Science' },
    { _id: 42, name: 'Stephanie Farmer', department: 'Science' },
    { _id: 43, name: 'Jugnu Gaur', department: 'Science' },
    { _id: 44, name: 'Bhavna Gehlot', department: 'Science' },
    { _id: 45, name: 'Rym Hannachi', department: 'Science' },
    { _id: 46, name: 'James Morehead', department: 'Science' },
    { _id: 47, name: 'Pia Murphy', department: 'Science' },
    { _id: 48, name: 'Annie Nguyen', department: 'Science' },
    { _id: 49, name: 'Carol Quach', department: 'Science' },
    { _id: 50, name: 'Dearborn Ramos', department: 'Science' },
    { _id: 51, name: 'Heida Shaw', department: 'Science' },
    { _id: 52, name: 'Yixuan Su', department: 'Science' },

    // Social Science
    { _id: 53, name: 'Mitchell Bathke', department: 'Social Science' },
    { _id: 54, name: 'Adam Bellows', department: 'Social Science' },
    { _id: 55, name: 'Karie Chamberlain', department: 'Social Science' },
    { _id: 56, name: 'Thomas Chamberlain', department: 'Social Science' },
    { _id: 57, name: 'Daniel Farmer', department: 'Social Science' },
    { _id: 58, name: 'Melanie Grossheider', department: 'Social Science' },
    { _id: 59, name: 'Holly Herrington', department: 'Social Science' },
    { _id: 60, name: 'Samir Khatri', department: 'Social Science' },
    { _id: 61, name: 'Drew Kravitz', department: 'Social Science' },
    { _id: 62, name: 'Rick La Torra', department: 'Social Science' },
    { _id: 63, name: 'Cynthia Oji-Marchese', department: 'Social Science' },
    { _id: 64, name: 'Julian Pont', department: 'Social Science' },
    { _id: 65, name: 'Jeanne Scheppach', department: 'Social Science' },
    { _id: 66, name: 'Diane Schumacher', department: 'Social Science' },
    { _id: 67, name: 'James Snavely', department: 'Social Science' },
    { _id: 68, name: 'Christopher Steele', department: 'Social Science' },
    { _id: 69, name: 'Jeffrey Vangene', department: 'Social Science' },
    { _id: 70, name: 'Andrew Wengel', department: 'Social Science' },

    // World Language
    { _id: 71, name: 'Herodytha Barahona', department: 'World Language' },
    { _id: 72, name: 'Sarah Colard', department: 'World Language' },
    { _id: 73, name: 'Allison Gardiner', department: 'World Language' },
    { _id: 74, name: 'Kristen Haynes', department: 'World Language' },
    { _id: 75, name: 'Yen Jen', department: 'World Language' },
    { _id: 76, name: 'Eun Hee Joo', department: 'World Language' },
    { _id: 77, name: 'Zhuoyan Li', department: 'World Language' },
    { _id: 78, name: 'Imin Moriya', department: 'World Language' },
    { _id: 79, name: 'Alycia Veytser', department: 'World Language' },

    // English Language Arts
    { _id: 80, name: 'Catherine Argabright-Harvill', department: 'English Language Arts' },
    { _id: 81, name: 'Mindy Benevides', department: 'English Language Arts' },
    { _id: 82, name: 'Joyce Chen', department: 'English Language Arts' },
    { _id: 83, name: 'Dylan Crowe', department: 'English Language Arts' },
    { _id: 84, name: 'Yolanda Davis', department: 'English Language Arts' },
    { _id: 85, name: 'Olga Dixit', department: 'English Language Arts' },
    { _id: 86, name: 'Susannah Faria', department: 'English Language Arts' },
    { _id: 87, name: 'Megan Fortayon', department: 'English Language Arts' },
    { _id: 88, name: 'John Hanavan', department: 'English Language Arts' },
    { _id: 89, name: 'Lena Hymel', department: 'English Language Arts' },
    { _id: 90, name: 'Noah Kopp', department: 'English Language Arts' },
    { _id: 91, name: 'Sara Kyles', department: 'English Language Arts' },
    { _id: 92, name: 'Caroline Lloyd', department: 'English Language Arts' },
    { _id: 93, name: 'Pamela Ly', department: 'English Language Arts' },
    { _id: 94, name: 'Megan Manley', department: 'English Language Arts' },
    { _id: 95, name: 'Sarah Militante', department: 'English Language Arts' },
    { _id: 96, name: 'Michael Morelli', department: 'English Language Arts' },
    { _id: 97, name: 'Hilary Morgenstern', department: 'English Language Arts' },
    { _id: 98, name: 'Reetika Naruka', department: 'English Language Arts' },
    { _id: 99, name: 'Jonathan Parks', department: 'English Language Arts' },
    { _id: 100, name: 'Mubina Parveen', department: 'English Language Arts' },
    { _id: 101, name: 'Brianna Phillips', department: 'English Language Arts' },
    { _id: 102, name: 'Brianne Smith', department: 'English Language Arts' },
    { _id: 103, name: 'Spencer Smith', department: 'English Language Arts' },
    { _id: 104, name: 'Erin Tanihara', department: 'English Language Arts' },
    { _id: 105, name: 'Jessica Tsuji', department: 'English Language Arts' },
    { _id: 106, name: 'Paul Vega', department: 'English Language Arts' },
    { _id: 107, name: 'Marie Ver Haar', department: 'English Language Arts' },
    { _id: 108, name: 'Jill Wharton', department: 'English Language Arts' },
    { _id: 109, name: 'Michelle Wilson', department: 'English Language Arts' },
    { _id: 110, name: 'Justin Worley', department: 'English Language Arts' },
    { _id: 111, name: 'Deanna Zappia', department: 'English Language Arts' },
    { _id: 112, name: 'Emily Chen', department: 'English Language Arts' },
    { _id: 113, name: 'Michael Hansen', department: 'Mathematics' },
    { _id: 114, name: 'Bradley Zenoni', department: 'Social Science' }
  ].map(teacher => ({
    ...teacher,
    email: generateEmail(teacher.name),
    avatarUrl: null,
    ratingStats: {
      count: 0,
      averageOverall: null,
      averagesByCategory: {
        overall: null,
        homeworkLoad: null,
        timeliness: null,
        rigor: null,
        supportiveness: null,
        clarity: null
      }
    },
    createdAt: new Date('2023-08-01'),
    updatedAt: new Date('2023-08-01')
  }));

  // No reviews initially - users will create them
  const initialReviews = [];


  // Fetch courses and departments
  const fetchCourses = async () => {
    try {
      const response = await api.get(`/courses`);
      setAllCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
      // Fallback to mock data if API fails
      setAllCourses([
        { id: 1, name: 'AP Calculus BC', code: 'MATH401', department: 'Mathematics' },
        { id: 2, name: 'AP Physics C', code: 'PHYS301', department: 'Science' },
        { id: 3, name: 'AP English Literature', code: 'ENG401', department: 'English' },
        { id: 4, name: 'AP Chemistry', code: 'CHEM301', department: 'Science' },
        { id: 5, name: 'AP US History', code: 'HIST301', department: 'Social Studies' },
        { id: 6, name: 'Algebra 2', code: 'MATH201', department: 'Mathematics' },
        { id: 7, name: 'Biology', code: 'BIO101', department: 'Science' },
        { id: 8, name: 'World Literature', code: 'ENG201', department: 'English' }
      ]);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get(`/departments`);
      setAllDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      // Fallback to mock data if API fails
      setAllDepartments([
        { id: 1, name: 'Mathematics' },
        { id: 2, name: 'Science' },
        { id: 3, name: 'English' },
        { id: 4, name: 'Social Studies' },
        { id: 5, name: 'World Languages' },
        { id: 6, name: 'Arts' },
        { id: 7, name: 'Physical Education' }
      ]);
    }
  };

  // Helper function to convert backend ratings to frontend format
  const convertBackendRatingsToFrontend = (backendRatings) => {
    return {
      overall: backendRatings.overall || 0,
      homeworkLoad: backendRatings.homework_load || 0,
      timeliness: backendRatings.timeliness_of_grading || 0,
      rigor: backendRatings.class_rigor || 0,
      supportiveness: backendRatings.teacher_supportiveness || 0,
      clarity: backendRatings.clarity_of_teaching || 0
    };
  };

  // Helper function to calculate teacher stats from reviews
  const calculateTeacherStatsFromReviews = (reviewsData) => {
    if (!reviewsData || reviewsData.length === 0) {
      return {
        count: 0,
        averageOverall: null,
        averagesByCategory: {
          overall: null,
          homeworkLoad: null,
          timeliness: null,
          rigor: null,
          supportiveness: null,
          clarity: null
        }
      };
    }

    const averagesByCategory = {};
    Object.keys(reviewCategories).forEach(category => {
      const sum = reviewsData.reduce((acc, review) => acc + (review.ratings[category] || 0), 0);
      averagesByCategory[category] = sum / reviewsData.length;
    });

    return {
      count: reviewsData.length,
      averageOverall: averagesByCategory.overall,
      averagesByCategory
    };
  };

  // Load real teacher data and reviews from backend API
  useEffect(() => {
    const loadTeachersAndReviews = async () => {
      try {
        // Fetch teachers from backend
        const teachersResponse = await api.get('/teachers');
        const teachersData = teachersResponse.data;

        // Fetch all reviews from backend
        const reviewsResponse = await api.get('/teachers/reviews/all');
        const reviewsData = reviewsResponse.data;

        // Calculate stats for each teacher
        const teachersWithStats = teachersData.map(teacher => {
          const teacherReviews = reviewsData.filter(r => r.teacher_id === teacher.id);
          const stats = calculateTeacherStatsFromReviews(teacherReviews);
          return {
            ...teacher,
            _id: teacher.id,
            ratingStats: stats,
            reviewCount: stats.count
          };
        });

        setTeachers(teachersWithStats);
        setReviews(reviewsData.map(r => ({
          _id: r._id,
          teacherId: r.teacher_id,
          courseId: r.course,
          ratings: convertBackendRatingsToFrontend(r.ratings),
          written: r.comment || '',
          createdBy: r.user_name,
          anonymous: r.user_name === 'Anonymous',
          userId: r.user_id,
          createdAt: new Date(r.created_at),
          updatedAt: new Date(r.updated_at),
          status: 'approved'
        })));
      } catch (error) {
        console.error('Error loading teachers and reviews:', error);
        // Fallback to local data
        setTeachers(realTeachers);
        setReviews(initialReviews);
      }
    };

    loadTeachersAndReviews();
    fetchCourses();
    fetchDepartments();
    
    setTimeout(() => setLoading(false), 1000);
  }, []);

  // Debounced search and filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      let filtered = teachers.filter(teacher => {
        const matchesSearch = teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            teacher.department.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesDepartment = !departmentFilter || teacher.department === departmentFilter;
        return matchesSearch && matchesDepartment;
      });

      // Apply sorting (aligned with specification)
      switch (sortBy) {
        case 'alpha_asc':
          filtered.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'alpha_desc':
          filtered.sort((a, b) => b.name.localeCompare(a.name));
          break;
        case 'rating_desc':
          filtered.sort((a, b) => {
            const aRating = a.ratingStats?.averageOverall;
            const bRating = b.ratingStats?.averageOverall;
            
            // null last
            if (aRating === null && bRating === null) return 0;
            if (aRating === null) return 1;
            if (bRating === null) return -1;
            
            // averageOverall desc, then count desc
            if (aRating !== bRating) return bRating - aRating;
            return (b.ratingStats?.count || 0) - (a.ratingStats?.count || 0);
          });
          break;
        case 'rating_asc':
          filtered.sort((a, b) => {
            const aRating = a.ratingStats?.averageOverall;
            const bRating = b.ratingStats?.averageOverall;
            
            // null last (reverse of desc)
            if (aRating === null && bRating === null) return 0;
            if (aRating === null) return 1;
            if (bRating === null) return -1;
            
            // averageOverall asc, then count asc
            if (aRating !== bRating) return aRating - bRating;
            return (a.ratingStats?.count || 0) - (b.ratingStats?.count || 0);
          });
          break;
        default:
          break;
      }

      // Calculate pagination
      const calculatedTotalPages = Math.ceil(filtered.length / teachersPerPage);
      const startIndex = (currentPage - 1) * teachersPerPage;
      const endIndex = startIndex + teachersPerPage;
      const currentTeachers = filtered.slice(startIndex, endIndex);

      setFilteredTeachers(currentTeachers);
      setTotalCount(filtered.length);
      setTotalPages(calculatedTotalPages);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, sortBy, departmentFilter, teachers, currentPage]);

  // Reset to first page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, departmentFilter]);

  // Reset review pagination when teacher changes
  useEffect(() => {
    setCurrentReviewPage(1);
  }, [selectedTeacher]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (courseDropdownOpen && !event.target.closest('.course-dropdown')) {
        setCourseDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [courseDropdownOpen]);

  const clearFilters = () => {
    setSearchTerm('');
    setSortBy('alpha_asc');
    setDepartmentFilter('');
  };

  // Course filtering for dropdown
  const getFilteredCourses = () => {
    let filtered = allCourses;

    if (courseSearchTerm) {
      filtered = filtered.filter(course =>
        course.name.toLowerCase().includes(courseSearchTerm.toLowerCase()) ||
        course.code.toLowerCase().includes(courseSearchTerm.toLowerCase())
      );
    }

    return filtered; // Show all matching courses
  };

  // Course selection handler
  const handleCourseSelect = (course) => {
    setReviewForm({ ...reviewForm, courseId: course.name });
    setCourseDropdownOpen(false);
    setCourseSearchTerm('');
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarSolid key={i} className="h-4 w-4 text-yellow-400" />);
    }
    if (hasHalfStar) {
      stars.push(<StarIcon key="half" className="h-4 w-4 text-yellow-400" />);
    }
    for (let i = stars.length; i < 5; i++) {
      stars.push(<StarIcon key={i} className="h-4 w-4 text-gray-300" />);
    }
    return stars;
  };

  // Rating stats recomputation
  const recomputeTeacherRatingStats = (teacherId, updatedReviews = reviews) => {
    const teacherReviews = updatedReviews.filter(review => 
      review.teacherId === teacherId && review.status === 'approved'
    );
    
    if (teacherReviews.length === 0) {
      return {
        count: 0,
        averageOverall: null,
        averagesByCategory: {
          overall: null,
          homeworkLoad: null,
          timeliness: null,
          rigor: null,
          supportiveness: null,
          clarity: null
        }
      };
    }

    const averagesByCategory = {};
    Object.keys(reviewCategories).forEach(category => {
      const sum = teacherReviews.reduce((acc, review) => acc + review.ratings[category], 0);
      averagesByCategory[category] = sum / teacherReviews.length;
    });

    return {
      count: teacherReviews.length,
      averageOverall: averagesByCategory.overall,
      averagesByCategory
    };
  };

  const updateTeacherRatingStats = (teacherId, updatedReviews = reviews) => {
    const newRatingStats = recomputeTeacherRatingStats(teacherId, updatedReviews);
    setTeachers(prevTeachers => 
      prevTeachers.map(teacher => 
        teacher._id === teacherId 
          ? { ...teacher, ratingStats: newRatingStats, reviewCount: newRatingStats.count, updatedAt: new Date() }
          : teacher
      )
    );
  };

  // Recalculate all teacher stats from reviews (used on initial load)
  const recalculateAllTeacherStats = (teachersData, reviewsData) => {
    return teachersData.map(teacher => {
      const newRatingStats = recomputeTeacherRatingStats(teacher._id, reviewsData);
      return {
        ...teacher,
        ratingStats: newRatingStats,
        reviewCount: newRatingStats.count,
        updatedAt: new Date()
      };
    });
  };

  // Review helper functions
  const getTeacherReviews = (teacherId) => {
    return reviews.filter(review => review.teacherId === teacherId);
  };

  const calculateCategoryAverages = (teacherId) => {
    // First try to get from teacher's ratingStats (preferred)
    const teacher = teachers.find(t => t._id === teacherId);
    if (teacher?.ratingStats?.averagesByCategory) {
      return teacher.ratingStats.averagesByCategory;
    }

    // Fallback: calculate from reviews
    const teacherReviews = getTeacherReviews(teacherId);
    if (teacherReviews.length === 0) return {};

    const averages = {};
    Object.keys(reviewCategories).forEach(category => {
      const sum = teacherReviews.reduce((acc, review) => acc + review.ratings[category], 0);
      averages[category] = sum / teacherReviews.length;
    });
    return averages;
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert frontend rating keys to backend format
      const backendRatings = {
        overall: reviewForm.ratings.overall,
        homework_load: reviewForm.ratings.homeworkLoad,
        timeliness_of_grading: reviewForm.ratings.timeliness,
        class_rigor: reviewForm.ratings.rigor,
        teacher_supportiveness: reviewForm.ratings.supportiveness,
        clarity_of_teaching: reviewForm.ratings.clarity
      };
      
      // Submit review to backend API
      const response = await api.post(`/teachers/${selectedTeacher._id}/reviews`, {
        teacher_id: selectedTeacher._id,
        course: reviewForm.courseId,
        ratings: backendRatings,
        comment: reviewForm.written || null
      });

      // Refresh reviews from backend
      const reviewsResponse = await api.get('/teachers/reviews/all');
      const reviewsData = reviewsResponse.data;
      
      // Update local state
      setReviews(reviewsData.map(r => ({
        _id: r._id,
        teacherId: r.teacher_id,
        courseId: r.course,
        ratings: convertBackendRatingsToFrontend(r.ratings),
        written: r.comment || '',
        createdBy: r.user_name,
        anonymous: r.user_name === 'Anonymous',
        userId: r.user_id,
        createdAt: new Date(r.created_at),
        updatedAt: new Date(r.updated_at),
        status: 'approved'
      })));

      // Update teacher stats
      const teacherReviews = reviewsData.filter(r => r.teacher_id === selectedTeacher._id);
      const stats = calculateTeacherStatsFromReviews(teacherReviews);
      setTeachers(prevTeachers => 
        prevTeachers.map(t => 
          t._id === selectedTeacher._id 
            ? { ...t, ratingStats: stats, reviewCount: stats.count }
            : t
        )
      );

      setEditingReview(null);

      // Reset form
      setReviewForm({
        courseId: '',
        ratings: {
          overall: 0,
          homeworkLoad: 0,
          timeliness: 0,
          rigor: 0,
          supportiveness: 0,
          clarity: 0
        },
        written: '',
        createdBy: null,
        anonymous: false,
        status: 'pending',
        edited: false
      });
      setWordCount(0);
      setShowReviewForm(false);
      alert('Review submitted successfully!');
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    }
  };

  const handleEditReview = (review) => {
    setReviewForm({
      courseId: review.courseId,
      ratings: review.ratings,
      written: review.written,
      createdBy: review.createdBy,
      anonymous: true,
      status: review.status,
      edited: review.edited
    });
    setWordCount((review.written || '').split(' ').filter(word => word.length > 0).length);
    setEditingReview(review);
    setShowReviewForm(true);
  };

  const handleDeleteReview = async (reviewId) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        const reviewToDelete = reviews.find(r => r._id === reviewId);
        
        // Delete review via API
        await api.delete(`/teachers/${reviewToDelete.teacherId}/reviews/${reviewId}`);
        
        // Refresh reviews from backend
        const reviewsResponse = await api.get('/teachers/reviews/all');
        const reviewsData = reviewsResponse.data;
        
        setReviews(reviewsData.map(r => ({
          _id: r._id,
          teacherId: r.teacher_id,
          courseId: r.course,
          ratings: convertBackendRatingsToFrontend(r.ratings),
          written: r.comment || '',
          createdBy: r.user_name,
          anonymous: r.user_name === 'Anonymous',
          userId: r.user_id,
          createdAt: new Date(r.created_at),
          updatedAt: new Date(r.updated_at),
          status: 'approved'
        })));
        
        // Update teacher stats
        const teacherReviews = reviewsData.filter(r => r.teacher_id === reviewToDelete.teacherId);
        const stats = calculateTeacherStatsFromReviews(teacherReviews);
        setTeachers(prevTeachers => 
          prevTeachers.map(t => 
            t._id === reviewToDelete.teacherId 
              ? { ...t, ratingStats: stats, reviewCount: stats.count }
              : t
          )
        );
        
        alert('Review deleted successfully!');
      } catch (error) {
        console.error('Error deleting review:', error);
        alert('Failed to delete review. Please try again.');
      }
    }
  };

  const handleWrittenReviewChange = (e) => {
    const text = e.target.value;
    const words = text.split(' ').filter(word => word.length > 0);
    
    if (words.length <= 300) {
      setReviewForm({ ...reviewForm, written: text });
      setWordCount(words.length);
    }
  };

  const renderInteractiveStars = (category, currentRating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <button
          key={i}
          type="button"
          onClick={() => setReviewForm({
            ...reviewForm,
            ratings: { ...reviewForm.ratings, [category]: i }
          })}
          className="focus:outline-none"
        >
          {i <= currentRating ? (
            <StarSolid className="h-6 w-6 text-yellow-400 hover:text-yellow-500" />
          ) : (
            <StarIcon className="h-6 w-6 text-gray-300 hover:text-yellow-400" />
          )}
        </button>
      );
    }
    return stars;
  };

  if (selectedTeacher) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        {/* Teacher Profile Page */}
        <div className="bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <button
              onClick={() => setSelectedTeacher(null)}
              className="mb-6 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium flex items-center gap-2 transition-colors"
            >
              ← Back to Teachers
            </button>
            
            {/* Header Section */}
            <div className="flex items-center gap-6 mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-gray-700 dark:to-gray-900 text-indigo-700 dark:text-gray-200 font-semibold text-2xl flex items-center justify-center ring-2 ring-indigo-400/40">
                {selectedTeacher.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {selectedTeacher.name}
                  </h1>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${departmentColors[selectedTeacher.department] || 'bg-gray-100 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-500/30'}`}>
                    {selectedTeacher.department}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {generateEmail(selectedTeacher.name)}
                </p>
                <div className="flex items-center gap-2">
                  {selectedTeacher.ratingStats?.count > 0 ? (
                    <div className="flex items-center gap-1 text-yellow-500 dark:text-yellow-400 text-sm">
                      <StarIcon className="h-4 w-4 fill-yellow-500 dark:fill-yellow-400" />
                      <span className="font-medium">{selectedTeacher.ratingStats.averageOverall.toFixed(1)}</span>
                      <span className="text-gray-500 dark:text-gray-400">({selectedTeacher.ratingStats.count} {selectedTeacher.ratingStats.count === 1 ? 'review' : 'reviews'})</span>
                    </div>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500 italic text-sm">No reviews yet</span>
                  )}
                </div>
              </div>
            </div>

            {/* Reviews Section */}
              <div className="space-y-6">
                {/* Category Averages */}
                {(() => {
                  const teacherReviews = getTeacherReviews(selectedTeacher._id);
                  const categoryAverages = calculateCategoryAverages(selectedTeacher._id);
                  
                  return (
                    <div className="bg-white dark:bg-gray-900/60 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-transparent">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-200 mb-6">Rating Breakdown</h3>
                      {teacherReviews.length > 0 ? (
                        <div className="space-y-3">
                          {Object.entries(reviewCategories).map(([key, label]) => (
                            <div key={key} className="flex items-center gap-3">
                              <span className="text-sm text-gray-600 dark:text-gray-400 w-40">
                                {label}
                              </span>
                              <div className="flex items-center gap-3 flex-1">
                                <div className="flex-1 bg-gray-200 dark:bg-gray-800 rounded-full h-2.5 overflow-hidden">
                                  <div
                                    className="bg-yellow-500 dark:bg-yellow-400 h-full rounded-full transition-all duration-300"
                                    style={{ width: `${(categoryAverages[key] / 5) * 100}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium min-w-[2.5rem] text-right">
                                  {(categoryAverages[key] || 0).toFixed(1)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400">No reviews yet. Be the first to review!</p>
                      )}
                    </div>
                  );
                })()}

                {/* Write Review Button */}
                <div className="bg-white dark:bg-gray-900/60 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-transparent">
                  <button
                    onClick={() => setShowReviewForm(true)}
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold py-3 rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md hover:shadow-lg"
                  >
                    {editingReview ? 'Edit Review' : 'Write a Review'}
                  </button>
                </div>

                {showReviewForm && (
                  <div className="bg-white dark:bg-gray-900/60 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-transparent">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-200 mb-6">
                      {editingReview ? 'Edit Review' : 'Write a Review'}
                    </h3>
                    
                    <form onSubmit={handleReviewSubmit} className="space-y-5">
                      {/* Course */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Course <span className="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <div className="relative course-dropdown">
                          <button
                            type="button"
                            onClick={() => setCourseDropdownOpen(!courseDropdownOpen)}
                            className="w-full px-4 py-2 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-xl text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 text-left flex items-center justify-between"
                          >
                            <span className={reviewForm.courseId ? 'text-gray-900 dark:text-gray-200' : 'text-gray-500 dark:text-gray-500'}>
                              {reviewForm.courseId || 'Select a course...'}
                            </span>
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>

                          {courseDropdownOpen && (
                            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-xl shadow-2xl">
                              {/* Search Header */}
                              <div className="p-3 border-b border-gray-200 dark:border-gray-800">
                                <input
                                  type="text"
                                  placeholder="Search courses..."
                                  value={courseSearchTerm}
                                  onChange={(e) => setCourseSearchTerm(e.target.value)}
                                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-indigo-500"
                                />
                              </div>

                              {/* Course List */}
                              <div className="max-h-64 overflow-y-auto">
                                {getFilteredCourses().length > 0 ? (
                                  getFilteredCourses().map(course => (
                                    <button
                                      key={course.id}
                                      type="button"
                                      onClick={() => handleCourseSelect(course)}
                                      className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800 last:border-b-0 focus:outline-none transition-colors"
                                    >
                                      <div className="flex justify-between items-center">
                                        <div>
                                          <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                                            {course.name}
                                          </div>
                                          <div className="text-xs text-gray-600 dark:text-gray-400">
                                            {course.code} • {course.department}
                                          </div>
                                        </div>
                                      </div>
                                    </button>
                                  ))
                                ) : (
                                  <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                                    No courses found
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Rating Categories */}
                      <div className="space-y-4">
                        <h4 className="text-md font-medium text-gray-900 dark:text-gray-200">Ratings <span className="text-red-500 dark:text-red-400">*</span></h4>
                        {Object.entries(reviewCategories).map(([key, label]) => (
                          <div key={key} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 sm:w-40">
                              {label}
                            </span>
                            <div className="flex flex-col items-end gap-1">
                              <div className="flex items-center space-x-1">
                                {renderInteractiveStars(key, reviewForm.ratings[key])}
                              </div>
                              {reviewForm.ratings[key] > 0 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                                  {getRatingHelperText(key, reviewForm.ratings[key])}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Written Review */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Written Review
                        </label>
                        <textarea
                          value={reviewForm.written}
                          onChange={handleWrittenReviewChange}
                          placeholder="Share your experience with this teacher..."
                          rows={4}
                          className="w-full px-4 py-2 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-xl text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 resize-none"
                        />
                        <div className="flex justify-between items-center mt-2">
                          <span className={`text-sm ${wordCount > 280 ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                            {wordCount}/300 words
                          </span>
                        </div>
                      </div>


                      {/* Form Actions */}
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => {
                            setShowReviewForm(false);
                            setEditingReview(null);
                            setReviewForm({
                              courseId: '',
                              ratings: {
                                overall: 0,
                                homeworkLoad: 0,
                                timeliness: 0,
                                rigor: 0,
                                supportiveness: 0,
                                clarity: 0
                              },
                              written: '',
                              createdBy: null,
                              anonymous: true,
                              status: 'pending',
                              edited: false
                            });
                            setWordCount(0);
                          }}
                          className="px-6 py-2 text-sm font-semibold text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-xl transition-all"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={!reviewForm.courseId || Object.values(reviewForm.ratings).some(r => r === 0)}
                          className="px-6 py-2 text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed rounded-xl transition-all"
                        >
                          {editingReview ? 'Update Review' : 'Submit Review'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Reviews List */}
                {(() => {
                  const teacherReviews = getTeacherReviews(selectedTeacher._id);
                  
                  // Pagination calculations
                  const totalReviews = teacherReviews.length;
                  const totalPages = Math.ceil(totalReviews / reviewsPerPage);
                  const startIndex = (currentReviewPage - 1) * reviewsPerPage;
                  const endIndex = startIndex + reviewsPerPage;
                  const paginatedReviews = teacherReviews.slice(startIndex, endIndex);
                  
                  return (
                    <div className="bg-white dark:bg-gray-900/60 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-transparent">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-200">
                          Reviews ({teacherReviews.length})
                        </h3>
                        {totalReviews > reviewsPerPage && (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Showing {startIndex + 1}-{Math.min(endIndex, totalReviews)} of {totalReviews}
                          </span>
                        )}
                      </div>
                      
                      {teacherReviews.length > 0 ? (
                        <>
                          <div className="space-y-5">
                            {paginatedReviews.map((review) => (
                            <div key={review._id} className="border-t border-gray-200 dark:border-gray-800/60 pt-5 first:border-t-0 first:pt-0">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="font-semibold text-gray-900 dark:text-gray-200">
                                      {(() => {

                                        return review.anonymous ? 'Anonymous' : (review.createdBy || 'Student');
                                      })()}
                                    </span>
                                    <span className="text-sm text-gray-400 dark:text-gray-500">•</span>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                      {review.courseId}
                                    </span>
                                    <span className="text-sm text-gray-400 dark:text-gray-500">•</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-500">
                                      {new Date(review.createdAt).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    {renderStars(review.ratings.overall)}
                                    <span className="text-sm text-gray-700 dark:text-gray-300 ml-2">
                                      {review.ratings.overall}/5 Overall
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Edit/Delete buttons for user's own reviews */}
                                {(() => {
                                  const currentUserId = user?.id || user?.student_id || user?.email || 'test-user-123';

                                  return review.userId === currentUserId;
                                })() && (
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => handleEditReview(review)}
                                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm font-medium transition-colors"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteReview(review._id)}
                                      className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium transition-colors"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                              
                              {/* Category Ratings */}
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                                {Object.entries(reviewCategories).slice(1).map(([key, label]) => (
                                  <div key={key} className="text-sm">
                                    <span className="text-gray-600 dark:text-gray-400">{label}:</span>
                                    <div className="flex items-center space-x-1 mt-1">
                                      {renderStars(review.ratings[key])}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Written Review */}
                              {review.written && (
                                <p className="text-gray-700 dark:text-gray-300 leading-snug">
                                  {review.written}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-center gap-2 mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
                            <button
                              onClick={() => setCurrentReviewPage(prev => Math.max(1, prev - 1))}
                              disabled={currentReviewPage === 1}
                              className="px-3 py-1 text-sm rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Previous
                            </button>
                            
                            {/* Page numbers */}
                            {[...Array(totalPages)].map((_, i) => {
                              const pageNum = i + 1;
                              // Show first, last, current, and ±2 around current
                              if (
                                pageNum === 1 ||
                                pageNum === totalPages ||
                                (pageNum >= currentReviewPage - 2 && pageNum <= currentReviewPage + 2)
                              ) {
                                return (
                                  <button
                                    key={pageNum}
                                    onClick={() => setCurrentReviewPage(pageNum)}
                                    className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                                      currentReviewPage === pageNum
                                        ? 'bg-indigo-600 text-white'
                                        : 'border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              } else if (
                                pageNum === currentReviewPage - 3 ||
                                pageNum === currentReviewPage + 3
                              ) {
                                return <span key={pageNum} className="px-2 text-gray-500">...</span>;
                              }
                              return null;
                            })}
                            
                            <button
                              onClick={() => setCurrentReviewPage(prev => Math.min(totalPages, prev + 1))}
                              disabled={currentReviewPage === totalPages}
                              className="px-3 py-1 text-sm rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Next
                            </button>
                          </div>
                        )}
                      </>
                      ) : (
                        <p className="text-gray-400 text-center py-8">
                          No reviews yet. Be the first to review {selectedTeacher.name}!
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Sticky Header with Blur */}
      <div className={`sticky top-16 z-20 bg-gray-50/95 dark:bg-gray-950/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 dark:supports-[backdrop-filter]:bg-gray-950/70 border-b border-gray-200 dark:border-gray-800/70 transition-transform duration-300 ${isHeaderVisible ? 'translate-y-0' : 'md:translate-y-0 -translate-y-full'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Header */}
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">Teachers Directory</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Explore and rate your DVHS teachers.</p>
          </div>

          {/* Search & Filters */}
          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[240px]">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search teachers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <ArrowUpIcon className="h-4 w-4 text-gray-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500 transition-all"
              >
                <option value="alpha_asc">A → Z</option>
                <option value="alpha_desc">Z → A</option>
                <option value="rating_desc">Best Rated</option>
                <option value="rating_asc">Lowest Rated</option>
              </select>
            </div>

            {/* Department Filter */}
            <div className="flex items-center gap-2">
              <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500 transition-all"
              >
                <option value="">All Departments</option>
                <option value="Fine and Applied Arts (VAPA)">Fine & Applied Arts</option>
                <option value="Mathematics">Mathematics</option>
                <option value="Physical Education">Physical Education</option>
                <option value="Science">Science</option>
                <option value="Social Science">Social Science</option>
                <option value="World Language">World Language</option>
                <option value="English Language Arts">English Language Arts</option>
              </select>
            </div>

            {/* Clear Filters */}
            {(searchTerm || sortBy !== 'alpha_asc' || departmentFilter) && (
              <button
                onClick={clearFilters}
                className="px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 rounded-lg transition-all"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Teachers Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-900/60 rounded-2xl shadow-md p-6 animate-pulse">
                <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-700 mx-auto mb-4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                <div className="h-3 bg-gray-700 rounded mb-4"></div>
                <div className="h-10 bg-gray-700 rounded-xl"></div>
              </div>
            ))}
          </div>
        ) : filteredTeachers.length === 0 ? (
          <div className="text-center py-16">
            <div className="mb-6 relative">
              <AcademicCapIcon className="h-24 w-24 text-gray-300 dark:text-gray-600 mx-auto" />
              <div className="absolute inset-0 flex items-center justify-center">
                <SparklesIcon className="h-12 w-12 text-indigo-400 opacity-50 animate-pulse" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No teachers found</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSortBy('alpha_asc');
                setDepartmentFilter('');
              }}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTeachers.map((teacher) => (
              <div
                key={teacher.id}
                className="bg-white dark:bg-gray-900/60 rounded-2xl shadow-md p-5 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer group border border-gray-200 dark:border-transparent"
                onClick={() => setSelectedTeacher(teacher)}
              >
                {/* Avatar */}
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-gray-700 dark:to-gray-900 text-indigo-700 dark:text-gray-200 font-semibold text-xl flex items-center justify-center ring-1 ring-indigo-400/40 group-hover:ring-indigo-400/60 transition-all">
                    {teacher.name.split(' ').map(n => n[0]).join('')}
                  </div>

                  {/* Name */}
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 text-center">
                    {teacher.name}
                  </h3>

                  {/* Email */}
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center truncate w-full px-2">
                    {generateEmail(teacher.name)}
                  </p>

                  {/* Rating */}
                  <div className="flex items-center justify-center">
                    {teacher.ratingStats?.count > 0 ? (
                      <div className="flex items-center gap-1 text-yellow-400 text-sm">
                        <StarIcon className="h-4 w-4 fill-yellow-400" />
                        <span className="font-medium">{teacher.ratingStats.averageOverall.toFixed(1)}</span>
                        <span className="text-gray-400">({teacher.ratingStats.count})</span>
                      </div>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 italic text-sm">No reviews yet</span>
                    )}
                  </div>

                  {/* View Profile Button */}
                  <button className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold py-2 rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md hover:shadow-lg">
                    View Profile
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-8 space-x-2">
            <button
              onClick={() => {
                setCurrentPage(prev => Math.max(prev - 1, 1));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
              // Show first page, last page, current page, and pages around current
              const showPage = page === 1 || page === totalPages || 
                              (page >= currentPage - 2 && page <= currentPage + 2);
              
              if (!showPage) {
                // Show ellipsis for gaps
                if (page === currentPage - 3 || page === currentPage + 3) {
                  return <span key={page} className="px-2 text-gray-400 dark:text-gray-500">...</span>;
                }
                return null;
              }
              
              return (
                <button
                  key={page}
                  onClick={() => {
                    setCurrentPage(page);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`px-4 py-2 rounded-xl transition-all font-medium ${
                    currentPage === page
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => {
                setCurrentPage(prev => Math.min(prev + 1, totalPages));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
            >
              Next
            </button>
          </div>
        )}
      </div>

    </div>
  );
}

function MainDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = location.pathname.slice(1) || 'dashboard';
  const [showSettings, setShowSettings] = useState(false);
  const [settingsSection, setSettingsSection] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [joinedClubs, setJoinedClubs] = useState(() => {
    const saved = localStorage.getItem('joinedClubs');
    return saved ? JSON.parse(saved) : [];
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  // Fetch transcript data
  const fetchTranscript = async () => {
    try {
      const response = await api.get(`/transcript/active`);
      setTranscript(response.data);
    } catch (error) {
      console.error('Error fetching transcript:', error);
      // Silently fail for dashboard - user can still navigate to upload transcript
      // Don't show error for 400 (parsing) or 404 (no transcript) as these are expected states
      if (error.response?.status !== 400 && error.response?.status !== 404) {
        console.warn('Unexpected error fetching transcript for dashboard:', error);
      }
    }
  };

  useEffect(() => {
    fetchTranscript();
  }, []);

  // Listen for transcript change event (when switching active transcript)
  useEffect(() => {
    const handleTranscriptChange = () => {

      fetchTranscript();
    };

    window.addEventListener('transcriptChanged', handleTranscriptChange);
    return () => window.removeEventListener('transcriptChanged', handleTranscriptChange);
  }, []);

  // Listen for custom event to open settings with specific section
  React.useEffect(() => {
    const handleOpenSettings = (event) => {
      setSettingsSection(event.detail || null);
      setShowSettings(true);
    };
    
    window.addEventListener('openSettings', handleOpenSettings);
    return () => window.removeEventListener('openSettings', handleOpenSettings);
  }, []);

  const handlePageChange = (page) => {
    navigate(`/${page}`);
    setShowSettings(false);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
  };

  if (showSettings) {
    return <Settings 
      onClose={() => {
        setShowSettings(false);
        setSettingsSection(null);
      }} 
      initialSection={settingsSection}
    />;
  }

  return (
    <div className="min-h-screen h-full bg-gray-50 dark:bg-gray-950 dark:text-gray-100">
      <Navbar 
        currentPage={currentPage} 
        onPageChange={handlePageChange} 
        onOpenSettings={() => setShowSettings(true)}
        onOpenMobileMenu={() => setMobileMenuOpen(true)}
      />
      
      {/* Mobile Menu */}
      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <MobileMenuSection title="Navigation">
          <MobileMenuItem 
            icon={HomeIcon}
            label="Dashboard"
            onClick={() => handlePageChange('dashboard')}
            active={currentPage === 'dashboard'}
          />
          <MobileMenuItem 
            icon={BookOpenIcon}
            label="Course Planner"
            onClick={() => handlePageChange('courses')}
            active={currentPage === 'courses'}
          />
          <MobileMenuItem 
            icon={DocumentTextIcon}
            label="My Transcript"
            onClick={() => handlePageChange('transcript')}
            active={currentPage === 'transcript'}
          />
          <MobileMenuItem 
            icon={AcademicCapIcon}
            label="Teachers"
            onClick={() => handlePageChange('teachers')}
            active={currentPage === 'teachers'}
          />
          <MobileMenuItem 
            icon={UserGroupIcon}
            label="Clubs"
            onClick={() => handlePageChange('clubs')}
            active={currentPage === 'clubs'}
          />
        </MobileMenuSection>
      </MobileMenu>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage transcript={transcript} joinedClubs={joinedClubs} onPageChange={handlePageChange} />} />
          <Route path="/courses" element={<CoursePlanner />} />
          <Route path="/transcript" element={<TranscriptViewer onPageChange={handlePageChange} />} />
          <Route path="/teachers" element={<Teachers />} />
          <Route path="/clubs" element={<Clubs />} />
        </Routes>
      </div>

      {/* Success Animations */}
      <SuccessCheckmark show={showSuccess} onComplete={() => setShowSuccess(false)} />
      <ConfettiAnimation show={showConfetti} onComplete={() => setShowConfetti(false)} />
      <Toast 
        message={toastMessage}
        type="success"
        show={showToast}
        onClose={() => setShowToast(false)}
      />
      
      {/* Footer */}
      <Footer />
    </div>
  );
}

function App() {
  // Initialize accent color from localStorage on app load
  React.useEffect(() => {
    const savedAccentColor = localStorage.getItem('accentColor') || 'indigo';
    const colorPalettes = {
      indigo: { 50: '#eef2ff', 100: '#e0e7ff', 200: '#c7d2fe', 300: '#a5b4fc', 400: '#818cf8', 500: '#6366f1', 600: '#4f46e5', 700: '#4338ca', 800: '#3730a3', 900: '#312e81' },
      blue: { 50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#93c5fd', 400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8', 800: '#1e40af', 900: '#1e3a8a' },
      purple: { 50: '#faf5ff', 100: '#f3e8ff', 200: '#e9d5ff', 300: '#d8b4fe', 400: '#c084fc', 500: '#a855f7', 600: '#9333ea', 700: '#7e22ce', 800: '#6b21a8', 900: '#581c87' },
      teal: { 50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 300: '#5eead4', 400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59', 900: '#134e4a' },
      amber: { 50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d', 400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309', 800: '#92400e', 900: '#78350f' },
      rose: { 50: '#fff1f2', 100: '#ffe4e6', 200: '#fecdd3', 300: '#fda4af', 400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c', 800: '#9f1239', 900: '#881337' },
      emerald: { 50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7', 400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857', 800: '#065f46', 900: '#064e3b' }
    };
    
    const palette = colorPalettes[savedAccentColor];
    if (palette) {
      Object.entries(palette).forEach(([shade, color]) => {
        document.documentElement.style.setProperty(`--accent-${shade}`, color);
      });
    }
  }, []);
  
  // Ensure body background extends beyond viewport
  React.useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    
    // Apply background to html and body
    const updateBackground = () => {
      const isDark = root.classList.contains('dark');
      body.style.backgroundColor = isDark ? 'rgb(3, 7, 18)' : 'rgb(249, 250, 251)';
      root.style.backgroundColor = isDark ? 'rgb(3, 7, 18)' : 'rgb(249, 250, 251)';
    };
    
    updateBackground();
    
    // Watch for theme changes
    const observer = new MutationObserver(updateBackground);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);
  
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
          <Analytics />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [signupWithTranscript, setSignupWithTranscript] = useState(null);

  // Check for transcript data in URL params on component mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const transcriptData = params.get('transcript_data');
    if (transcriptData) {
      try {
        setSignupWithTranscript(JSON.parse(decodeURIComponent(transcriptData)));
        // Clean up the URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (e) {
        console.error('Error parsing transcript data:', e);
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600"></div>
      </div>
    );
  }

  // If user is authenticated, show the main dashboard
  if (user) {
    return <MainDashboard />;
  }

  // If not authenticated, show public routes
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/landing" replace />} />
      <Route path="/landing" element={<Landing />} />
      <Route path="/login" element={<LoginForm initialTranscriptData={signupWithTranscript} isLogin={true} />} />
      <Route path="/register" element={<LoginForm initialTranscriptData={signupWithTranscript} isLogin={false} />} />
      <Route path="*" element={<Navigate to="/landing" replace />} />
    </Routes>
  );
}

export default App;
